/**
 * @fileoverview OpenLayers map wrapper providing tile layer management,
 * GeoJSON layers, result highlighting, and custom controls.
 * @module map/OpenLayersMap
 */

import { TileSetSwitcherControl } from './TileSetSwitcherControl.js';
import { ResetViewControl } from './ResetViewControl.js';
import { FitExtentControl } from './FitExtentControl.js';
import { OverlayControl } from './OverlayControl.js';
import { RegionControl } from './RegionControl.js';
import { MapEffectControl } from './MapEffectControl.js';
import { InfoButtonControl } from './InfoButtonControl.js';
import { PdfViewerControl } from './PdfViewerControl.js';
import { ZoomLevelControl } from './ZoomLevelControl.js';
import { calculateHoverTextHtml } from '../utils/nynfuncs.js';
import { doFitBounds, scaleExtent, computeTileGridExtent } from '../utils/jrgeofuncs.js';
import { DEBUG_CBM, DEBUG_REFRESH, DEBUG_NETWORK, DEBUG_TILES, SEARCH_TYPES, PTYPES, MAP_CONSTANTS, MAP_PERSON_COLORS, MAP_ACTION_COLORS, MAP_CUSTOMS, AUTO_ZOOM_STYLE, MAP_AUX_OVERLAY_LAYERS, MAP_LAYER_ZINDICES, MAP_YELLOW_COLORS, MAP_EFFECTS, MAPAPP_DEFAULTS, PMTILES_CONSTANTS, FIELD_ISBOOKMARK, DOM_ELEMENTS, MAP_MODES } from '../constants.js';
import { jrhash, replaceHtmlColorOpacity, normalizeCssColorToHex } from '../utils/jrfuncs.js';
import { getStaticBasePathOrDefault } from '../config/pageConfig.js';


// Cached 1×1 transparent PNG used to terminate failed PMTiles tile loads.
// We previously assigned a `data:image/png;base64,…` URI directly to
// `tile.getImage().src`, but Chrome occasionally rejected the URI with
// `ERR_INVALID_URL` after long-lived/suspended sessions, and once it started
// the failure cascaded across every subsequent tile. Using a blob: URL built
// once at module load bypasses the data-URI parsing path entirely.
let _blankPngBlobUrl = null;
function getBlankPngBlobUrl() {
    if (_blankPngBlobUrl) return _blankPngBlobUrl;
    // Minimal 1×1 transparent PNG, hand-built so we don't rely on base64.
    const bytes = new Uint8Array([
        0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A,             // PNG signature
        0x00, 0x00, 0x00, 0x0D, 0x49, 0x48, 0x44, 0x52,             // IHDR length + tag
        0x00, 0x00, 0x00, 0x01, 0x00, 0x00, 0x00, 0x01,             // width=1, height=1
        0x08, 0x06, 0x00, 0x00, 0x00, 0x1F, 0x15, 0xC4, 0x89,       // 8-bit RGBA, IHDR CRC
        0x00, 0x00, 0x00, 0x0D, 0x49, 0x44, 0x41, 0x54,             // IDAT length + tag
        0x78, 0x9C, 0x62, 0x00, 0x01, 0x00, 0x00, 0x05,             // zlib-deflated single transparent pixel
        0x00, 0x01, 0x0D, 0x0A, 0x2D, 0xB4, 0x00, 0x00, 0x00,       // IDAT data + CRC
        0x00, 0x49, 0x45, 0x4E, 0x44, 0xAE, 0x42, 0x60, 0x82        // IEND
    ]);
    const blob = new Blob([bytes], { type: 'image/png' });
    _blankPngBlobUrl = URL.createObjectURL(blob);
    return _blankPngBlobUrl;
}



// ---------------------------------------------------------------------------
// PMTILES HTTP-CACHE POLICY
//
// Letting the browser's HTTP cache hold .pmtiles range responses is worth having --
// nginx serves them with a long immutable Cache-Control (see the
// `location ~* ^/media/.+\.pmtiles$` block in nginx_config.conf), so a reload can
// re-read the pmtiles header and root directory straight off disk instead of over
// the wire. But it is NOT universally safe, and the split is by ENGINE, not taste:
//
//  - Chromium is the only engine that stores partial responses as "sparse" disk
//    cache entries and can serve a LATER, DIFFERENT byte range out of one. It is
//    also the one with the bug: on WINDOWS it fails such requests outright with
//    net::ERR_CACHE_OPERATION_NOT_SUPPORTED rather than refetching, which shows up
//    as permanently blank map tiles. The pmtiles library itself carries exactly this
//    workaround -- its FetchSource sets a `chromeWindowsNoCache` flag from the same
//    UA test and forces cache:'no-store', with NO version gate (verified against the
//    pmtiles@4.4.0 bundle this app loads). So this is upstream's shipping position,
//    not a guess.
//  - Firefox has never cached byte-range responses, and WebKit does not store 206s
//    at all, so there this whole mechanism is a harmless no-op: reads always hit the
//    network, exactly as they do today.
//
// Net: this is a Chromium-on-not-Windows optimization, with a hard kill switch
// (PMTILES_CONSTANTS.USE_BROWSER_HTTP_CACHE) and a runtime self-heal (the degrade
// path in getBytes below).
// ---------------------------------------------------------------------------
let _pmtilesHttpCacheAllowed = null;
function pmtilesBrowserHttpCacheAllowed() {
    if (_pmtilesHttpCacheAllowed !== null) {
        return _pmtilesHttpCacheAllowed;
    }
    if (!PMTILES_CONSTANTS.USE_BROWSER_HTTP_CACHE) {
        _pmtilesHttpCacheAllowed = false;
    } else {
        const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
        const isWindows = (userAgent.indexOf('Windows') > -1);
        // Chrome, Chromium, Edge, Opera and Brave all ship the same net stack.
        const isChromium = /Chrome|Chromium|Edg|OPR|Brave/.test(userAgent);
        _pmtilesHttpCacheAllowed = !(isWindows && isChromium);
    }
    if (DEBUG_TILES) console.log(`PMTiles: browser HTTP caching of range reads is ${_pmtilesHttpCacheAllowed ? 'ENABLED' : 'DISABLED'} for this browser.`);
    return _pmtilesHttpCacheAllowed;
}

// Archive urls whose cached range reads have already failed at runtime. Module scope
// on purpose: it must outlive an individual Source so that one unservable cache entry
// costs at most ONE failed request per page load, even if a layer (and therefore its
// Source) is ever rebuilt for the same archive.
const _pmtilesHttpCacheDeniedUrls = new Set();


/**
 * A PMTiles Source (implements the pmtiles library's Source interface) that reads the archive via
 * HTTP range requests when the server supports byte serving, and otherwise transparently falls back
 * to downloading the whole archive once and slicing requested ranges out of memory.
 *
 * Why: PMTiles' built-in FetchSource issues `Range` requests and throws
 * "Server returned no content-length header or content-length exceeding request. Check that your
 * storage backend supports HTTP Byte Serving." when a backend ignores `Range` and returns the full
 * file. This fallback keeps pmtiles maps working on any backend, staying efficient (206 partial
 * reads) where byte serving IS supported.
 *
 * CACHING STRATEGY: read #1 ALWAYS probes with cache:'no-store'. Only once the origin has PROVEN it
 * byte-serves (a real 206 came back) do later reads switch to cache:'default'. Probing this way is
 * the whole point: it guarantees a whole-file 200 body is never written into the disk cache under
 * this url, which is the main way that cache ends up in a state it cannot serve ranges from. The
 * upgrade is further gated per-browser -- see pmtilesBrowserHttpCacheAllowed() above.
 *
 * WHOLE-FILE FALLBACK: capped. Production worlds ship 600+MB archives, and buffering one of those
 * into the tab is a guaranteed stall and, on mobile, a crash. Above the cap we fail loudly instead.
 */
class PMTilesRangeOrWholeFileSource {
    /** @param {string} url - URL of the .pmtiles archive (already cache-busted with ?cachev=) */
    constructor(url) {
        this.url = url;
        this._fullBuffer = null;        // cached whole-file ArrayBuffer once we learn ranges are unsupported
        this._fullBufferPromise = null; // in-flight whole-file fetch (dedupes concurrent reads)
        // RequestCache mode for the next range read. Starts pessimistic; upgraded on the first 206.
        this._cacheMode = 'no-store';
    }

    /** @returns {string} stable cache key for this archive */
    getKey() {
        return this.url;
    }

    /**
     * Issues one range GET at the current cache mode. Split out of getBytes() so the
     * degrade-and-retry path can re-issue the identical request under a different cache mode
     * without duplicating the fetch options.
     * @param {number} offset - byte offset to read from
     * @param {number} length - number of bytes to read
     * @param {AbortSignal} [signal]
     * @returns {Promise<Response>}
     * @private
     */
    _fetchRange(offset, length, signal) {
        return fetch(this.url, {
            signal,
            cache: this._cacheMode,
            headers: { Range: `bytes=${offset}-${offset + length - 1}` }
        });
    }

    /**
     * @param {number} offset - byte offset to read from
     * @param {number} length - number of bytes to read
     * @param {AbortSignal} [signal]
     * @returns {Promise<{data: ArrayBuffer, etag?: string, cacheControl?: string, expires?: string}>}
     */
    async getBytes(offset, length, signal) {
        // Already know the server ignores Range — serve everything from the cached whole file.
        if (this._fullBuffer) {
            return { data: this._fullBuffer.slice(offset, offset + length) };
        }
        if (this._fullBufferPromise) {
            const buffer = await this._fullBufferPromise;
            return { data: buffer.slice(offset, offset + length) };
        }

        let response;
        try {
            response = await this._fetchRange(offset, length, signal);
        } catch (error) {
            // An abort is the caller's own doing — never reinterpret it as a cache problem.
            if ((error && error.name === 'AbortError') || (signal && signal.aborted)) {
                throw error;
            }
            if (this._cacheMode === 'no-store') {
                // Nothing left to fall back to: a genuine network / DNS / CORS failure.
                console.error(`ERROR: PMTiles range read of "${this.url}" (bytes ${offset}-${offset + length - 1}) failed:`, error);
                throw error;
            }
            // We were using the browser HTTP cache and the request never reached the network. The
            // signature case is Chromium answering net::ERR_CACHE_OPERATION_NOT_SUPPORTED for a
            // disk cache entry it holds but cannot serve a range from (truncated by an aborted
            // download, stored as a plain 200 body, or a sparse entry it has decided it cannot
            // use). Chromium does NOT recover by refetching, so the only cure is to stop consulting
            // the cache for this archive. A plain offline blip lands here too; that is acceptable,
            // the only cost being that we lose caching for this archive for the rest of the session.
            console.warn(
                `PMTiles: range read of "${this.url}" failed while the browser HTTP cache was in use ` +
                `(${(error && error.message) ? error.message : error}). Treating this as an unservable cache ` +
                `entry: disabling the HTTP cache for this archive and retrying once.`
            );
            this._cacheMode = 'no-store';
            _pmtilesHttpCacheDeniedUrls.add(this.url);
            try {
                response = await this._fetchRange(offset, length, signal);
            } catch (retryError) {
                console.error(`ERROR: PMTiles range read of "${this.url}" failed again with the HTTP cache bypassed:`, retryError);
                throw retryError;
            }
        }

        if (response.status === 206) {
            // Proper HTTP byte serving. Now — and only now — is it safe to let the browser cache
            // later reads: the origin honours Range, so nothing we do from here can write a
            // whole-file 200 body into the cache under this url.
            if ((this._cacheMode === 'no-store')
                && (!_pmtilesHttpCacheDeniedUrls.has(this.url))
                && pmtilesBrowserHttpCacheAllowed()) {
                this._cacheMode = 'default';
                if (DEBUG_TILES) console.log(`PMTiles: "${this.url}" answered 206; later range reads may now use the browser HTTP cache.`);
            }
            return {
                data: await response.arrayBuffer(),
                etag: response.headers.get('ETag') || undefined,
                cacheControl: response.headers.get('Cache-Control') || undefined,
                expires: response.headers.get('Expires') || undefined
            };
        }

        if (response.status === 200) {
            // Server ignored the Range header and returned the whole file. Decide from
            // Content-Length ALONE — calling response.arrayBuffer() first would already have
            // committed the tab to the full allocation, which is the entire thing we are avoiding.
            // Content-Length is CORS-safelisted, so this works cross-origin with no
            // Access-Control-Expose-Headers entry. A missing one is treated as "too big": we cannot
            // bound the download, and upstream pmtiles refuses that case too.
            const contentLengthHeader = response.headers.get('Content-Length');
            const contentLength = (contentLengthHeader === null) ? Number.NaN : Number(contentLengthHeader);
            const capBytes = PMTILES_CONSTANTS.WHOLE_FILE_FALLBACK_MAX_BYTES;
            if ((!Number.isFinite(contentLength)) || (contentLength > capBytes)) {
                // Stop the transfer; without this the browser keeps streaming a body we refuse to read.
                if (response.body) {
                    response.body.cancel().catch(() => { /* nothing useful to do here */ });
                }
                const sizeText = Number.isFinite(contentLength)
                    ? `${(contentLength / (1024 * 1024)).toFixed(1)} MB`
                    : 'an unknown size (no Content-Length header)';
                const message =
                    `PMTiles: "${this.url}" was requested as a byte range but the server returned the ENTIRE file ` +
                    `(HTTP 200, ${sizeText}), which exceeds the ${(capBytes / (1024 * 1024)).toFixed(0)} MB whole-file fallback cap ` +
                    `(PMTILES_CONSTANTS.WHOLE_FILE_FALLBACK_MAX_BYTES in constants.js). Refusing to buffer it into the tab -- ` +
                    `this backend does not support HTTP byte serving. nginx does (every docker image here fronts /media/ with it), ` +
                    `and so does the DEBUG-only range view in core/views.py, so seeing this means neither is in the path.`;
                console.error(`ERROR: ${message}`);
                throw new Error(message);
            }
            // Small enough to be worth buffering: cache it once and slice locally from here on
            // (subsequent reads take the _fullBuffer fast path above).
            console.warn(
                `PMTiles: server returned the full file (HTTP 200, ${(contentLength / (1024 * 1024)).toFixed(1)} MB) instead of ` +
                `partial content (206) for a range request to "${this.url}". The backend does not support HTTP byte ` +
                `serving; falling back to downloading the whole archive once and serving ranges from memory.`
            );
            // Clear the memo if the body read fails, otherwise every later getBytes() would await a
            // permanently rejected promise.
            this._fullBufferPromise = response.arrayBuffer().catch((error) => {
                this._fullBufferPromise = null;
                throw error;
            });
            this._fullBuffer = await this._fullBufferPromise;
            return { data: this._fullBuffer.slice(offset, offset + length) };
        }

        if (response.body) {
            response.body.cancel().catch(() => { /* nothing useful to do here */ });
        }
        throw new Error(`PMTiles fetch for "${this.url}" failed with HTTP ${response.status}.`);
    }
}



/**
 * OpenLayers map wrapper class.
 * Manages tile layers, GeoJSON POI layers, result highlighting layers,
 * hover popups, and custom controls (layer switcher, reset view).
 * @class
 */
export class OpenLayersMapManager {
    /**
     * Creates a new OpenLayersMapManager instance.
     * Initializes layer storage, control references, and style cache.
     * @param {Object} mapApp - The MapApp instance
     */
    constructor(mapApp) {
        this.mapApp = mapApp;

        // Core map instance, and the div initialize() creates for it
        this.map = null;
        this.mapDiv = null;

        // Layer management
        this.layers = new Map();
        this.resultLayers = new Map(); // Store result layers by search tab type

        // for efficiency
        this.layerHighlight = null;

        //
        this.firstBounds = null;

        // Tile layer state
        this.tileSet = {
            layers: [],        // Store all available tile layers
            currentIndex: 0    // Track currently visible tile layer
        };

        // Controls
        this.controls = {
            tileSetSwitcher: null,
            resetView: null,
            fitExtent: null,
            overlayControl: null,
            regionControl: null,
            infoButton: null
        };

        // Popup state
        this.popupState = {
            overlay: null,     // Popup overlay for hover tooltips
            element: null      // Popup DOM element
        };

        // Style caching for performance
        this.styleCache = {};

        // style config system
        this.markerStyleConfig = {};
        this.styleId = {};

        // custom config
        this.customConfig = {}

        // Raster (pixel coordinate) mode support
        this.mapMode = MAP_MODES.GEOGRAPHIC;
        this.rasterProjection = null;
        this.rasterExtent = null;
        this.rasterResolutions = null;

        // PMTiles library class (lazy-loaded)
        this.PMTilesClass = null;

        // Register proj4 with OpenLayers if available
        this.initializeProjections();
    }




    // ==========================================
    // SETUP AND INIT
    // ==========================================



    /**
     * Initializes the OpenLayers map in the specified container.
     * @param {string} containerId - DOM element ID to render the map into
     * @param {Object} [config={}] - Map configuration options
     * @param {Array<number>} [config.center=[0,0]] - Initial center [lon, lat]
     * @param {number} [config.defaultViewZoom=2] - Initial zoom level
     * @param {number} [config.minViewZoom=0] - Minimum allowed zoom
     * @param {number} [config.maxViewZoom=20] - Maximum allowed zoom
     * @param {number} [config.viewAngle=0] - Initial rotation in degrees
     * @returns {ol.Map} The created OpenLayers map instance
     */
    initialize(containerId, config = {}) {
        const {
            center = [0, 0],
            defaultViewZoom = 2,
            minViewZoom = 0,
            maxViewZoom = 20,
            viewAngle = 0,
            style = {},
        } = config;

        // Create map container div (owned by this manager: destroy() removes it again, or every
        // world switch would leave another empty #map behind with a duplicate id)
        const container = document.getElementById(containerId);
        const mapDiv = document.createElement('div');
        mapDiv.id = 'map';
        container.appendChild(mapDiv);
        this.mapDiv = mapDiv;

        // Convert angle from degrees to radians for OpenLayers
        // OpenLayers rotation is in radians, positive values rotate clockwise
        const rotation = (viewAngle * Math.PI) / 180;

        // Create popup element for hover tooltips
        this.popupState.element = document.createElement('div');
        this.popupState.element.className = 'ol-popup';
        this.popupState.element.style.display = 'none';

        // Create popup overlay
        // ATTN: This controls the offset of the popup relative to the cursor on first mouseover; it would be better if it were releative to marker pos
        this.popupState.overlay = new ol.Overlay({
            element: this.popupState.element,
            positioning: 'bottom-center',
            stopEvent: false,
            offset: MAP_CONSTANTS.POPUP_OFFSET
        });

        // Build view based on map mode (geographic vs raster)
        let mapView;
        if (this.mapMode === MAP_MODES.RASTER) {
            // Raster mode: pixel coordinate system, no geographic projection
            // Note: extent is set on the projection (not the view) so tiles are bounded
            // but panning is not over-constrained when zoomed out
            mapView = new ol.View({
                projection: this.rasterProjection,
                center: center,
                zoom: defaultViewZoom,
                minZoom: minViewZoom,
                maxZoom: maxViewZoom,
                rotation: rotation,
                resolutions: this.rasterResolutions,
            });
        } else {
            // Geographic mode: Web Mercator projection
            mapView = new ol.View({
                center: ol.proj.fromLonLat(center),
                zoom: defaultViewZoom,
                minZoom: minViewZoom,
                maxZoom: maxViewZoom,
                rotation: rotation,
            });
        }

        // Initialize OpenLayers map (target is the element itself, not the 'map' id string)
        this.map = new ol.Map({
            target: mapDiv,
            layers: [],
            view: mapView,
            controls: ol.control.defaults.defaults({
                attribution: true,
                zoom: true,
                rotate: false, // Always hide rotation reset control
            }),
            // User-driven rotation is disabled: the map has no use for it, and on a tablet a
            // stray two-finger twist would leave the map crooked with the reset-north control
            // hidden (rotate: false above), so there is no obvious way back to square.
            //
            // These MUST be interaction options, not control options -- they lived in the
            // controls block above for a while, where ol.control.defaults silently ignored
            // them and the default PinchRotate stayed installed, which is exactly how tablet
            // users could still rotate.
            //
            // NOT done by setting enableRotation:false on the View: that would pin rotation
            // to 0 and throw away each world's configured viewAngle (the v6mb/v6pm nynoir
            // worlds are deliberately laid out at -29.1 degrees). Removing the gestures keeps
            // the configured angle while stopping the user from changing it.
            interactions: ol.interaction.defaults.defaults({
                altShiftDragRotate: false,  // no Alt+Shift drag-rotate (desktop)
                pinchRotate: false          // no two-finger twist-rotate (touch/tablet)
            }),
            overlays: [this.popupState.overlay]
        });

        // Add hover interaction for markers
        this.initializeHoverInteraction();

        // Add click interaction for markers
        this.initializeClickInteraction();

        // style
        this.customConfig = config.custom;

        // set background color
        this.setBackgroundMapColor(this.getCustomConfigValueOrDefault('mapCanvasBackgroundColor', MAPAPP_DEFAULTS.MAP_CANVAS_BACKGROUND_COLOR));

        return this.map;
    }


    setBackgroundMapColor(color) {
        // set background color for our map div (dom id DOM_ELEMENTS.ID_MAP)
        const mapDiv = this.mapDiv || document.getElementById(DOM_ELEMENTS.ID_MAP);
        mapDiv.style.backgroundColor = color;
        // console.log(`ATTN: setting background of map color to: ${color}`)
    }







    //
    // NEW MARKER STYLESYSTEM

    /**
     * Sets the style configuration object used for building marker styles.
     * @param {Object} val - Style configuration mapping style IDs to style definitions
     */
    setMarkerStyleConfig(val) {
        this.markerStyleConfig = val;
        // PREPROCESS IT
        this.preProcessMarkerStyleConfig(this.markerStyleConfig);
    }
    /**
     * Resets the style cache, forcing all styles to be rebuilt on next render.
     */
    resetStyleCache() {
        // just delete any cached styles so it will rebuild
        this.styleCache = {};
    }

    /**
     * Sets the style data for a given category by looking up a style ID in the style configuration.
     * @param {string} category - The category key (e.g., 'yellow')
     * @param {string} styleId - The style ID to look up in the style configuration
     */
    setMarkerStyleId(category, styleId) {
        // set the style data for yellow pins
        let styleData = this.buildStyleFromKey(this.markerStyleConfig, styleId);
        if (!styleData) {
            styleData = this.buildStyleFromKey(this.markerStyleConfig, MAPAPP_DEFAULTS.DEFAULT_MARKER_STYLE);
            if (!styleData) {
                throw new Error(`buildStyleFromKey: no matching item found for key "${styleId}"`);
            }
        }
        this.styleId[category] = styleData;
    }

    preProcessMarkerStyleConfig(configArray) {
        // convert image paths
        for (const item of configArray) {
            item['image'] = this.resolveStaticPath('images/' + item['path']);
            // console.log(`STYLE ITEM: ${JSON.stringify(item)}`)
        }
    }

    buildStyleFromKey(configArray, key) {
        for (const item of configArray) {
            if (item.caption === key || item.path === key) {
                return item;
            }
        }
        return null;
    }


    /**
     * Gets the style data for a given category.
     * @param {string} category - The category key
     * @returns {Object|undefined} The style data object, or undefined if not set
     */
    getStyleDataByCategory(category) {
        return this.styleId[category];
    }

    resolveStaticPath(path) {
        return this.mapApp.resolveStaticPath(path);
    }






    /**
     * Initializes custom projections using proj4.
     * Registers EPSG:2263 (NAD83 / New York Long Island) for coordinate transformation.
     * @private
     */
    initializeProjections() {
        if (typeof proj4 !== 'undefined' && typeof ol !== 'undefined') {
            // Define EPSG:2263 - NAD83 / New York Long Island (ftUS)
            proj4.defs('EPSG:2263', '+proj=lcc +lat_1=41.03333333333333 +lat_2=40.66666666666666 +lat_0=40.16666666666666 +lon_0=-74 +x_0=300000.0000000001 +y_0=0 +ellps=GRS80 +datum=NAD83 +to_meter=0.3048006096012192 +no_defs');

            // Register proj4 with OpenLayers
            ol.proj.proj4.register(proj4);
        }
    }


    /**
     * Initializes raster (pixel coordinate) mode for non-geocoded maps.
     * Must be called before initialize() if the world uses raster mode.
     * @param {number} imageWidth - Width of the source raster image in pixels
     * @param {number} imageHeight - Height of the source raster image in pixels
     * @param {number} [maxViewZoom] - Maximum view zoom level (for oversampling beyond native tiles)
     */
    initializeRasterProjection(imageWidth, imageHeight, maxViewZoom) {
        this.mapMode = MAP_MODES.RASTER;
        this.rasterTileResolutions = this._computeRasterResolutions(imageWidth, imageHeight);
        this.rasterNativeMaxZoom = this.rasterTileResolutions.length - 1;

        // Extend resolutions for the View to allow zooming beyond native tile levels.
        // At zoom levels beyond native max, OpenLayers automatically oversamples
        // (upscales) the highest available tiles.
        if (maxViewZoom != null && maxViewZoom > this.rasterNativeMaxZoom) {
            this.rasterResolutions = [...this.rasterTileResolutions];
            let res = this.rasterTileResolutions[this.rasterNativeMaxZoom];
            for (let z = this.rasterNativeMaxZoom + 1; z <= maxViewZoom; z++) {
                res /= 2;
                this.rasterResolutions.push(res);
            }
        } else {
            this.rasterResolutions = this.rasterTileResolutions;
        }

        // gdal2tiles --profile=raster coordinate system:
        //   x: 0 to imageWidth (left to right)
        //   y: -imageHeight to 0 (top of image is y=0, bottom is y=-imageHeight)
        //
        // Tile alignment fix: gdal2tiles aligns tiles from the BOTTOM (TMS convention)
        // while OpenLayers aligns from the TOP (origin). When the image height isn't a
        // perfect multiple of tile coverage, the partial tile row ends up at opposite ends,
        // causing a per-zoom vertical shift. We fix this by padding the grid extent height
        // to an exact multiple of the coarsest tile coverage (maxResolution * tileSize).
        // Since all resolutions are powers of 2, this makes tile coverage divide evenly
        // at ALL zoom levels, eliminating partial rows and alignment differences.
        const tileSize = 256;
        const maxResolution = this.rasterResolutions[0];
        const maxTileCoverage = maxResolution * tileSize;
        const paddedHeight = Math.ceil(imageHeight / maxTileCoverage) * maxTileCoverage;
        const gridTop = paddedHeight - imageHeight;

        // Extent: from image bottom (-imageHeight) up to padded top (gridTop).
        // The area from y=0 to y=gridTop is above the image (empty padding).
        const extent = [0, -imageHeight, imageWidth, gridTop];
        this.rasterProjection = new ol.proj.Projection({
            code: 'PIXELS',
            units: 'pixels',
            extent: extent
        });
        this.rasterExtent = extent;
        this.rasterImageWidth = imageWidth;
        this.rasterImageHeight = imageHeight;
        this.rasterPaddedHeight = paddedHeight;
        this.rasterGridTop = gridTop;
    }


    /**
     * Computes resolution array for raster tile grid matching gdal2tiles --profile=raster output.
     * @param {number} width - Image width in pixels
     * @param {number} height - Image height in pixels
     * @returns {Array<number>} Resolutions from coarsest to finest
     * @private
     */
    _computeRasterResolutions(width, height) {
        // gdal2tiles --profile=raster uses power-of-2 resolutions.
        // At max zoom (native resolution), 1 source pixel = 1 tile pixel (resolution = 1).
        // Each lower zoom level doubles the resolution.
        // Number of levels = ceil(log2(maxDim / tileSize)) + 1
        const tileSize = 256;
        const maxDim = Math.max(width, height);
        const numLevels = Math.ceil(Math.log2(maxDim / tileSize)) + 1;
        const resolutions = [];
        for (let i = 0; i < numLevels; i++) {
            resolutions.push(Math.pow(2, numLevels - 1 - i));
        }
        return resolutions;
    }


    isRasterMode() {
        return this.mapMode === MAP_MODES.RASTER;
    }


    /**
     * Builds the OpenLayers tile grid matching gdal2tiles --profile=raster output.
     * Origin at top-left of the padded extent ensures tiles align with gdal2tiles'
     * bottom-up generation at every zoom level (see initializeRasterProjection).
     * @returns {ol.tilegrid.TileGrid}
     * @private
     */
    _createRasterTileGrid() {
        return new ol.tilegrid.TileGrid({
            extent: this.rasterExtent,
            origin: [0, this.rasterGridTop],
            resolutions: this.rasterTileResolutions,
            tileSize: 256
        });
    }


    /**
     * Maps a raster-grid tile coordinate to its TMS row and checks it against the
     * tiles gdal2tiles actually generated (tiles in the padded area above/right of
     * the image don't exist).
     *
     * The padded grid has totalRows = paddedHeight / (tileSize * resolution), which
     * is always a whole number since paddedHeight is a multiple of
     * maxResolution * tileSize and all resolutions are powers of 2.
     * @param {number} z - Zoom level
     * @param {number} x - Tile column
     * @param {number} y - Tile row (top-down, OpenLayers grid convention)
     * @returns {{valid: boolean, yTms: number}}
     * @private
     */
    _rasterTileCoordInfo(z, x, y) {
        const tileSize = 256;
        const resolution = this.rasterTileResolutions[z];
        const totalRows = this.rasterPaddedHeight / (tileSize * resolution);
        const yTms = totalRows - 1 - y;
        const actualRows = Math.ceil(this.rasterImageHeight / (tileSize * resolution));
        const actualCols = Math.ceil(this.rasterImageWidth / (tileSize * resolution));
        const valid = yTms >= 0 && yTms < actualRows && x >= 0 && x < actualCols;
        return { valid, yTms };
    }



    /**
     * Initializes click interaction for map markers.
     * This is what tells the mapApp when user clicks on a marker or a blank space on map
     * @private
     */
    initializeClickInteraction() {
        this.map.on('click', (evt) => {
            // Ctrl+click creates a custom user bookmark at the clicked location
            if (evt.originalEvent.ctrlKey) {
                this.mapApp.createCustomUserBookmarkAtLocation(evt.coordinate[0], evt.coordinate[1]);
                return;
            }

            const feature = this.map.forEachFeatureAtPixel(evt.pixel, (feature) => {
                return feature;
            }, {
                layerFilter: (layer) => this.isInteractiveLayer(layer)
            });

            if (feature) {
                this.userClicksOnMarker(feature);
            } else {
                // User clicked on empty area - perform reverse search at that location
                this.userClicksOnNonMarkerPointOnMap(evt.coordinate);
            }
        });
    }



    /**
     * Initializes hover interaction for map markers.
     * Shows popup tooltips on hover and changes cursor to pointer.
     * @private
     */
    initializeHoverInteraction() {
        this.currentHoverFeature = null;

        // Handle pointer move events
        this.map.on('pointermove', (evt) => {
            if (evt.dragging) {
                return;
            }

            const pixel = this.map.getEventPixel(evt.originalEvent);
            const layerFilterFn = (layer) => this.isInteractiveLayer(layer);

            // Find feature at pixel (one hit-test serves both the cursor and the popup)
            const feature = this.map.forEachFeatureAtPixel(pixel, (feature) => {
                return feature;
            }, {
                layerFilter: layerFilterFn
            });

            // Change cursor on hover
            this.map.getTargetElement().style.cursor = feature ? 'pointer' : '';

            this.userHoversOverFeature(feature, evt.coordinate);
        });
    }
    

    /**
     * Initializes the layer switcher control if multiple tile layers are registered.
     * Adds the control to the map and wires up layer change callbacks.
     */
    initializeTileSetSwitcher() {
        if (this.tileSet.layers.length <= 1) {
            return; // No need for switcher with 0 or 1 layers
        }

        const layerConfigs = this.tileSet.layers.map(info => ({
            name: info.name
        }));

        this.controls.tileSetSwitcher = new TileSetSwitcherControl({
            layers: layerConfigs,
            onLayerChange: (layer, index) => {
                this.activateTileSet(index);
                // Notify MapApp of layer change for state persistence
                if (this.mapApp && this.mapApp.onTileSetChange) {
                    this.mapApp.onTileSetChange(index);
                }
            }
        });

        this.map.addControl(this.controls.tileSetSwitcher);
    }


    /**
     * Initializes the reset view control that returns the map to initial bounds.
     * Requires MapApp reference to be set via setMapApp() first.
     */
    initializeResetViewControl() {
        if (!this.mapApp) {
            console.error('ERROR: MapApp reference not set. Call setMapApp() before initializing reset control.');
            return;
        }

        this.controls.resetView = new ResetViewControl({
            onReset: () => {
                if (this.mapApp && this.mapApp.fitBoundsInitial) {
                    this.mapApp.fitBoundsInitial();
                }
            }
        });

        this.map.addControl(this.controls.resetView);
    }


    /**
     * Initializes the fit extent control that zooms to fit all visible POI markers.
     * Requires MapApp reference to be set via setMapApp() first.
     */
    initializeFitExtentControl() {
        if (!this.mapApp) {
            console.error('ERROR: MapApp reference not set. Call setMapApp() before initializing fit extent control.');
            return;
        }

        this.controls.fitExtent = new FitExtentControl({
            onFitExtent: () => {
                this.mapApp.autoZoomPanToVisibleFeaturesExplicitlyByRequest();
            }
        });

        this.map.addControl(this.controls.fitExtent);
    }


    /**
     * Initializes the overlay visibility control for toggling result layer visibility.
     * Requires MapApp reference to be set via setMapApp() first.
     * @param {Object} [initialState={}] - Initial visibility state from StateManager
     */
    initializeOverlayControl(initialState = {}) {
        if (!this.mapApp) {
            console.error('ERROR: MapApp reference not set. Call setMapApp() before initializing overlay control.');
            return;
        }

        this.controls.overlayControl = new OverlayControl({
            visibilityState: initialState,
            onVisibilityChange: (layerId, visible, allState) => {
                this.handleOverlayVisibilityChange(layerId, visible, allState);
            }
        });

        this.map.addControl(this.controls.overlayControl);
    }


    /**
     * Initializes the region selection control.
     * @param {Array} regions - Array of region objects from AuxDataManager
     * @param {string} selectedRegion - Initially selected region from StateManager
     */
    initializeRegionControl(regions, selectedRegion) {
        if (!this.mapApp) {
            console.error('ERROR: MapApp reference not set. Call setMapApp() before initializing region control.');
            return;
        }

        this.controls.regionControl = new RegionControl({
            regions: regions,
            selectedRegion: selectedRegion,
            onRegionChange: (regionId) => {
                this.mapApp.onRegionChange(regionId);
            }
        });

        this.map.addControl(this.controls.regionControl);
    }


    /**
     * Gets the region control instance.
     * @returns {RegionControl|null} The region control or null if not initialized
     */
    getRegionControl() {
        return this.controls.regionControl;
    }


    /**
     * Initializes the map effect control for switching between visual effect presets.
     * @param {string} [selectedEffect='none'] - ID of the initially selected effect
     */
    initializeMapEffectControl(selectedEffect = 'none') {
        this.controls.mapEffect = new MapEffectControl({
            effects: MAP_EFFECTS,
            selectedEffect: selectedEffect,
            onEffectChange: (effectId) => {
                this.applyMapEffect(effectId);
                if (this.mapApp && this.mapApp.onMapEffectChange) {
                    this.mapApp.onMapEffectChange(effectId);
                }
            }
        });

        this.map.addControl(this.controls.mapEffect);

        // Apply the initial effect
        this.applyMapEffect(selectedEffect);
    }


    /**
     * Applies a visual effect to the map tile layers via a CSS custom property.
     * @param {string} effectId - ID of the effect to apply (from MAP_EFFECTS)
     */
    applyMapEffect(effectId) {
        const effect = MAP_EFFECTS.find(e => e.id === effectId);
        const filterValue = effect ? effect.filter : 'none';
        const target = this.map.getTargetElement();
        if (target) {
            target.style.setProperty('--tile-filter', filterValue);
        }
    }


    /**
     * Initializes the info button control at the bottom-right of the map.
     * @param {Function} onInfo - Callback invoked when the info button is clicked
     */
    initializeInfoButtonControl(onInfo) {
        this.controls.infoButton = new InfoButtonControl({
            onInfo: onInfo
        });

        this.map.addControl(this.controls.infoButton);
    }


    /**
     * Initializes the PDF viewer toggle control at the upper-left of the map.
     * @param {Function} onToggle - Callback invoked when the button is clicked
     */
    initializePdfViewerControl(onToggle) {
        this.controls.pdfViewer = new PdfViewerControl({
            onToggle: onToggle
        });

        this.map.addControl(this.controls.pdfViewer);
    }


    /**
     * Initializes the zoom level display control at the bottom-left of the map.
     */
    initializeZoomLevelControl() {
        this.controls.zoomLevel = new ZoomLevelControl();
        this.map.addControl(this.controls.zoomLevel);
    }


    /**
     * Wires up sibling relationships between dropdown controls so that opening
     * one dropdown automatically closes the others.
     */
    wireDropdownControlSiblings() {
        const dropdownControls = [
            this.controls.tileSetSwitcher,
            this.controls.overlayControl,
            this.controls.regionControl,
            this.controls.mapEffect
        ].filter(Boolean);

        for (const control of dropdownControls) {
            const siblings = dropdownControls.filter(c => c !== control);
            control.setSiblingControls(siblings);
        }
    }
















    // ==========================================
    // PMTILES LIBRARY LOADING
    // ==========================================


    /**
     * Dynamically loads the self-hosted PMTiles library if not already loaded.
     * Must be called (and awaited) before creating any pmtiles tile layers.
     *
     * The library is a vendored, self-contained ESM bundle (pmtiles + its fflate
     * dependency inlined -- the raw npm/jsdelivr dist is NOT self-contained) built
     * by `npm run build:pmtiles` and committed under js/libs/pmtiles/. The import
     * path is computed at runtime, which keeps esbuild from resolving it into
     * main.bundle.js -- so the library stays lazy-loaded in both dev and prod,
     * fetched only when a pmtiles world is actually activated. The version lives
     * in the filename, so the long /static/ cache life can never serve a stale
     * copy across upgrades. Deliberately no CDN fallback: removing the external
     * dependency is the point.
     * @async
     * @returns {Function} The PMTiles class constructor
     */
    async loadPMTilesLibrary() {
        if (this.PMTilesClass) {
            return this.PMTilesClass;
        }
        try {
            const staticBasePath = getStaticBasePathOrDefault();
            const pmtilesUrl = staticBasePath + 'js/libs/pmtiles/pmtiles-4.5.0.js';
            const pmtilesModule = await import(pmtilesUrl);
            this.PMTilesClass = pmtilesModule.PMTiles;
            if (DEBUG_TILES) console.log('PMTiles library loaded successfully.');
            return this.PMTilesClass;
        } catch (error) {
            console.error('ERROR: Failed to load PMTiles library:', error);
            throw error;
        }
    }


    // ==========================================
    // SETUP STUFF
    // ==========================================


    /**
     * Creates an OpenLayers tile layer from URL pattern and options.
     * Supports both standard XYZ and TMS (Y-flipped) tile formats.
     * @param {string} url - Tile URL pattern with {z}/{x}/{y} placeholders
     * @param {Object} [options={}] - Layer options
     * @param {string} [options.tileFormat='xyz'] - Tile format: 'xyz' or 'tms'
     * @returns {ol.layer.Tile} The created tile layer
     * @private
     */
    _createTileLayer(url, options = {}) {
        const {
            name = 'tile-layer',
            minZoom = 0,
            maxZoom = 20,
            attribution = '',
            opacity = 1.0,
            tileFormat = 'xyz' // 'xyz' or 'tms'
        } = options;

        let tileSource;

        // Raster (pixel-coordinate) worlds use a custom tile grid; the pmtiles/mbtiles
        // branches below combine that grid with their own tile loading, so old worlds
        // can ship single-file archives without leaving pixel space.
        const isRaster = this.mapMode === MAP_MODES.RASTER;

        // TMS Y-flip URL function (shared by geographic and raster modes)
        const tmsTileUrlFunction = (tileCoord) => {
            if (!tileCoord) {
                return undefined;
            }

            const z = tileCoord[0];
            const x = tileCoord[1];
            const y = tileCoord[2];

            // For TMS, Y coordinate is flipped: y_tms = (2^z - 1) - y
            // XYZ uses y where 0 is at the top
            // TMS uses y where 0 is at the bottom
            const yTms = (1 << z) - y - 1;

            const tileUrl = url
                .replace('{z}', z.toString())
                .replace('{x}', x.toString())
                .replace('{y}', yTms.toString());

            return tileUrl;
        };

        if (tileFormat === 'pmtiles') {
            // PMTiles: single-file tile archive loaded via the PMTiles library.
            // The PMTiles class must be preloaded via loadPMTilesLibrary() before this point.
            if (!this.PMTilesClass) {
                console.error('ERROR: PMTiles library not loaded. Call loadPMTilesLibrary() first.');
                return null;
            }
            // Wrap the url in a Source that falls back to whole-file fetching when the backend does
            // not support HTTP byte serving (e.g. Django's dev static server), instead of throwing.
            const pmtilesInstance = new this.PMTilesClass(new PMTilesRangeOrWholeFileSource(url));
            // Fetch header to determine tile MIME type and log metadata
            let pmtilesMimeType = 'image/png';
            pmtilesInstance.getHeader().then((header) => {
                // PMTiles TileType enum: 1=MVT, 2=PNG, 3=JPEG, 4=WebP, 5=AVIF
                const mimeTypes = { 1: 'application/x-protobuf', 2: 'image/png', 3: 'image/jpeg', 4: 'image/webp', 5: 'image/avif' };
                if (header.tileType && mimeTypes[header.tileType]) {
                    pmtilesMimeType = mimeTypes[header.tileType];
                }
                if (DEBUG_TILES) console.log(`PMTiles header: tileType=${header.tileType} (${pmtilesMimeType}), minZoom=${header.minZoom}, maxZoom=${header.maxZoom}`);
            }).catch((error) => {
                // Without this the rejection is unhandled. Now that the whole-file fallback is size
                // capped, a backend that cannot byte-serve REJECTS here instead of quietly buffering
                // 600MB -- and the message explaining why would otherwise surface as a context-free
                // "Uncaught (in promise)" with no clue which layer it belonged to.
                //
                // Routed through mapApp.addError (which logs, console.errors AND raises the sidebar
                // error banner) rather than a bare console.error, because when this fires the tile
                // layer renders BLANK: someone staring at an empty map has no reason to open
                // devtools, so the one actionable instruction has to be on screen. This is the layer
                // that knows about the UI -- PMTilesRangeOrWholeFileSource deliberately keeps its
                // own error purely technical.
                const errorDetail = (error && error.message) ? error.message : error;
                this.mapApp.addError(
                    `Map tiles could not be loaded for this world, so the map will appear blank. ` +
                    `This world's map is a .pmtiles archive, which needs a web server that supports byte-range requests. ` +
                    `To switch to a world that does not need one, use the gear icon at the top of the sidebar -> ` +
                    `"Select new world data file..." and choose the "(mb)" variant of this world if there is one. ` +
                    `Technical detail: ${errorDetail}`
                );
            });
            const pmtilesSourceOptions = {
                cacheSize: MAPAPP_DEFAULTS.TILE_SOURCE_CACHE_SIZE,
                attributions: attribution,
                tileUrlFunction: (tileCoord) => {
                    // Return a synthetic URL encoding the tile coordinates;
                    // actual data loading is handled by tileLoadFunction below.
                    if (!tileCoord) return undefined;
                    // Raster mode: skip tiles in the padded area. The grid's top-down y
                    // is passed through unchanged — it equals the archive's XYZ y, since
                    // the archive was packed by flipping gdal2tiles' TMS rows and the
                    // padded raster grid has exactly 2^z rows at every zoom.
                    if (isRaster && !this._rasterTileCoordInfo(tileCoord[0], tileCoord[1], tileCoord[2]).valid) {
                        return undefined;
                    }
                    return `pmtiles://${tileCoord[0]}/${tileCoord[1]}/${tileCoord[2]}`;
                },
                tileLoadFunction: (tile, src) => {
                    const parts = src.replace('pmtiles://', '').split('/').map(Number);
                    const z = parts[0];
                    const x = parts[1];
                    const y = parts[2];
                    pmtilesInstance.getZxy(z, x, y).then((response) => {
                        if (response && response.data) {
                            const blob = new Blob([response.data], { type: pmtilesMimeType });
                            const objectUrl = URL.createObjectURL(blob);
                            const image = tile.getImage();
                            // released whichever way the image ends: a tile whose bytes fail to
                            // decode fires error, not load, and used to keep its blob for the page's life
                            image.onload = image.onerror = () => {
                                image.onload = image.onerror = null;
                                URL.revokeObjectURL(objectUrl);
                            };
                            image.src = objectUrl;
                        } else {
                            if (DEBUG_TILES) console.warn(`PMTiles: no data for tile z=${z} x=${x} y=${y}`);
                            // Mark the tile as loaded with a transparent blob so it transitions
                            // out of LOADING state; without this, stuck tiles fill OpenLayers'
                            // tile queue and block all rendering.
                            tile.getImage().src = getBlankPngBlobUrl();
                        }
                    }).catch((err) => {
                        if (DEBUG_TILES) console.warn(`PMTiles: error loading tile z=${z} x=${x} y=${y}:`, err);
                        // Same fix: ensure the tile doesn't stay stuck in LOADING state on errors.
                        tile.getImage().src = getBlankPngBlobUrl();
                    });
                }
            };
            if (isRaster) {
                // The custom grid governs which zooms/tiles exist; min/maxZoom would
                // wrongly clip it (they refer to the default mercator grid).
                pmtilesSourceOptions.tileGrid = this._createRasterTileGrid();
                pmtilesSourceOptions.projection = this.rasterProjection;
            } else {
                pmtilesSourceOptions.minZoom = minZoom;
                pmtilesSourceOptions.maxZoom = maxZoom;
            }
            tileSource = new ol.source.XYZ(pmtilesSourceOptions);
        } else if (tileFormat === 'mbtiles') {
            // MBTiles: tiles are served via a /mbtiles/ endpoint that reads the SQLite database.
            // URL pattern: /mbtiles/{mbtiles_path}/{z}/{x}/{y} — the server handles TMS y-flip internally.
            // The endpoint resolves mbtiles_path relative to MEDIA_ROOT, so strip the /media/
            // url prefix (the old media-absolute form only worked when MEDIA_ROOT was exactly
            // /media, as in the containers, and produced a /mbtiles//media/... double slash).
            // The url is resolved first (against the site root, so "media/x" still reads as "/media/x"),
            // so a world loaded through an absolute same-origin ?worldPath=https://host/media/cbm/...
            // gives the same path as a root-relative one (it used to build /mbtiles/https://host/media/...
            // and get no tiles at all). The query string (the cache buster) is kept.
            const archiveUrl = new URL(url, window.location.origin + '/');
            if (archiveUrl.origin !== window.location.origin) {
                // the endpoint reads only THIS server's media folder, so the same path is read here
                // (right for www.nynoir.org vs nynoir.org); said so rather than thrown, since this
                // runs lazily, from the tile switcher too
                this.mapApp.addWarning(`The .mbtiles archive "${url}" is on another site; its tiles are read from the same path on this one.`);
            }
            const mediaRelativePath = archiveUrl.pathname.replace(/^\/media\//, '');
            const tileUrl = '/mbtiles/' + mediaRelativePath + '/{z}/{x}/{y}' + archiveUrl.search;
            if (DEBUG_TILES) console.log(`MBTiles tile URL pattern: ${tileUrl}`);
            if (isRaster) {
                // Raster world served from an mbtiles archive: use the raster tile grid,
                // and send the grid's top-down y UNFLIPPED. The server applies the global
                // TMS flip (2^z - 1 - y), which recovers the stored TMS row exactly,
                // because the padded raster grid has 2^z rows at every zoom.
                tileSource = new ol.source.XYZ({
                    cacheSize: MAPAPP_DEFAULTS.TILE_SOURCE_CACHE_SIZE,
                    tileGrid: this._createRasterTileGrid(),
                    projection: this.rasterProjection,
                    attributions: attribution,
                    tileUrlFunction: (tileCoord) => {
                        if (!tileCoord) {
                            return undefined;
                        }
                        const z = tileCoord[0];
                        const x = tileCoord[1];
                        const y = tileCoord[2];
                        if (!this._rasterTileCoordInfo(z, x, y).valid) {
                            return undefined;
                        }
                        return tileUrl
                            .replace('{z}', z.toString())
                            .replace('{x}', x.toString())
                            .replace('{y}', y.toString());
                    }
                });
            } else {
                tileSource = new ol.source.XYZ({
                    cacheSize: MAPAPP_DEFAULTS.TILE_SOURCE_CACHE_SIZE,
                    url: tileUrl,
                    minZoom: minZoom,
                    maxZoom: maxZoom,
                    attributions: attribution
                });
            }
        } else if (this.mapMode === MAP_MODES.RASTER) {
            // Raster mode with a legacy {z}/{x}/{y}.png pyramid (tilemapresourceXmlUrl
            // configs): custom tile grid + XYZ->TMS row conversion, since gdal2tiles
            // --profile=raster names its tile files by TMS row.
            tileSource = new ol.source.XYZ({
                cacheSize: MAPAPP_DEFAULTS.TILE_SOURCE_CACHE_SIZE,
                tileGrid: this._createRasterTileGrid(),
                projection: this.rasterProjection,
                attributions: attribution,
                tileUrlFunction: (tileCoord) => {
                    if (!tileCoord) {
                        return undefined;
                    }
                    const z = tileCoord[0];
                    const x = tileCoord[1];
                    const y = tileCoord[2];

                    const { valid, yTms } = this._rasterTileCoordInfo(z, x, y);
                    if (!valid) {
                        return undefined;
                    }

                    return url
                        .replace('{z}', z.toString())
                        .replace('{x}', x.toString())
                        .replace('{y}', yTms.toString());
                }
            });
        } else if (tileFormat === 'tms') {
            // Geographic mode with TMS tiles (GDAL2Tiles output)
            tileSource = new ol.source.XYZ({
                cacheSize: MAPAPP_DEFAULTS.TILE_SOURCE_CACHE_SIZE,
                // Do not provide url when using tileUrlFunction
                minZoom: minZoom,
                maxZoom: maxZoom,
                attributions: attribution,
                tileUrlFunction: tmsTileUrlFunction
            });
        } else {
            // Geographic mode with standard XYZ tiles (Y origin at top)
            tileSource = new ol.source.XYZ({
                cacheSize: MAPAPP_DEFAULTS.TILE_SOURCE_CACHE_SIZE,
                url: url,
                minZoom: minZoom,
                maxZoom: maxZoom,
                attributions: attribution
            });
        }

        // Attach debug listeners if enabled
        if (DEBUG_NETWORK) {
            this._attachTileDebugListeners(tileSource, tileFormat.toUpperCase());
        }

        const tileLayer = new ol.layer.Tile({
            source: tileSource,
            className: 'ol-tile-layer',
            preload: MAPAPP_DEFAULTS.TILE_LAYER_PRELOAD,
            opacity: opacity,
            // set visible false during creation, it will be made visible later
            visible: true,
        });

        return tileLayer;
    }

    /**
     * Attach debug event listeners to a tile source to report tile fetch timing and cache status.
     * @param {ol.source.XYZ} source - The tile source
     * @param {string} format - Format label for logging (e.g., 'TMS', 'XYZ')
     */
    _attachTileDebugListeners(source, format) {
        if (!DEBUG_NETWORK) return;

        // Map to track tile load start times by coordinate string
        const tileStartTimes = new Map();

        source.on('tileloaderror', (event) => {
            const coord = event.tile.getTileCoord();
            const coordKey = coord.join('/');
            tileStartTimes.delete(coordKey);
            console.error(`ERROR: [${format}] Tile load error:`, coord, event.tile.src_);
        });

        source.on('tileloadstart', (event) => {
            const coord = event.tile.getTileCoord();
            const coordKey = coord.join('/');
            const tileUrl = event.tile.src_;
            tileStartTimes.set(coordKey, { startTime: performance.now(), url: tileUrl });
            console.debug(`DEBUG: [${format}] Tile fetch start: ${coordKey} -> ${tileUrl}`);
        });

        source.on('tileloadend', (event) => {
            const coord = event.tile.getTileCoord();
            const coordKey = coord.join('/');
            const startData = tileStartTimes.get(coordKey);

            if (startData) {
                const elapsedMs = (performance.now() - startData.startTime).toFixed(2);
                const cacheInfo = this._getTileCacheStatus(startData.url);
                console.debug(`DEBUG: [${format}] Tile fetch complete: ${coordKey} | ${elapsedMs}ms | ${cacheInfo}`);
                tileStartTimes.delete(coordKey);
            } else {
                console.debug(`DEBUG: [${format}] Tile fetch complete: ${coordKey} (no start time recorded)`);
            }
        });
    }

    /**
     * Check if a tile was served from browser cache using the Performance API.
     * @param {string} url - The tile URL to check
     * @returns {string} Cache status description
     * @private
     */
    _getTileCacheStatus(url) {
        if (!window.performance || !performance.getEntriesByName) {
            return 'cache status unknown (Performance API unavailable)';
        }

        const entries = performance.getEntriesByName(url, 'resource');
        if (entries.length === 0) {
            return 'cache status unknown (no performance entry)';
        }

        // Get the most recent entry for this URL
        const entry = entries[entries.length - 1];

        // Determine cache status based on transferSize and duration
        // transferSize = 0 typically means served from cache (memory or disk)
        // Also check if duration is very small as another indicator
        if (entry.transferSize === 0) {
            // Distinguish between memory cache (very fast) and disk cache
            if (entry.duration < 5) {
                return 'FROM MEMORY CACHE';
            } else {
                return 'FROM DISK CACHE';
            }
        } else if (entry.transferSize > 0) {
            return `FROM NETWORK (${(entry.transferSize / 1024).toFixed(1)} KB)`;
        } else {
            // transferSize might be undefined in some cases (cross-origin without CORS)
            return 'cache status unknown (cross-origin or no timing info)';
        }
    }




    // ==========================================
    // DELETING STUFF
    // ==========================================


    /**
     * Destroys the map and cleans up all resources.
     * Clears layers, style cache, and removes the map from DOM.
     */
    destroy() {
        if (this.map) {
            // dispose() (not just setTarget(null)) clears the map's controls, interactions and
            // overlays: each control gets setMap(null), which is where the dropdown controls drop
            // their document-level click listener -- without it those listeners kept the whole
            // previous world's map (layers, features) alive after every world switch
            this.map.dispose();
            this.map = null;
            this.layers.clear();
        }
        // and remove the div initialize() appended to the container
        if (this.mapDiv) {
            this.mapDiv.remove();
            this.mapDiv = null;
        }
        this.styleCache = {};
    }









    // ==========================================
    // ACCESSOR-LIKE STUFF
    // ==========================================

    /**
     * Gets the underlying OpenLayers map instance.
     * @returns {ol.Map} The OpenLayers map
     */
    getMap() {
        return this.map;
    }


    /**
     * Gets the overlay control instance.
     * @returns {OverlayControl|null} The overlay control or null if not initialized
     */
    getOverlayControl() {
        return this.controls.overlayControl;
    }

    /**
     * Sets the map view to a specific location and zoom level.
     * @param {number} lon - Longitude coordinate
     * @param {number} lat - Latitude coordinate
     * @param {number} zoom - Zoom level
     */
    setView(lon, lat, zoom) {
        const view = this.map.getView();
        if (this.mapMode === MAP_MODES.RASTER) {
            view.setCenter([lon, lat]);
        } else {
            view.setCenter(ol.proj.fromLonLat([lon, lat]));
        }
        view.setZoom(zoom);
    }

    /**
     * Gets the current map view state.
     * @returns {Object} View object with lon, lat, and zoom properties
     */
    getView() {
        const view = this.map.getView();
        if (this.mapMode === MAP_MODES.RASTER) {
            const center = view.getCenter();
            return {
                lon: center[0],
                lat: center[1],
                zoom: view.getZoom()
            };
        }
        const center = ol.proj.toLonLat(view.getCenter());
        return {
            lon: center[0],
            lat: center[1],
            zoom: view.getZoom()
        };
    }


    /**
     * Determines if a layer should respond to user interactions (click, hover).
     * Used to filter out non-interactive layers like the highlight, anchor, and region layers.
     * @param {ol.layer.Layer} layer - The layer to check
     * @returns {boolean} True if the layer should be interactive
     */
    isInteractiveLayer(layer) {
        // The highlight, anchor, and region layers should not respond to interactions
        const highlightLayer = this.resultLayers.get(MAP_AUX_OVERLAY_LAYERS.HIGHLIGHT);
        const regionLayer = this.resultLayers.get(MAP_AUX_OVERLAY_LAYERS.REGION);
        const anchorLayer = this.resultLayers.get(MAP_AUX_OVERLAY_LAYERS.ANCHOR);
        const debugLayer = this.resultLayers.get(MAP_AUX_OVERLAY_LAYERS.DEBUG);
        return (layer !== highlightLayer && layer !== regionLayer && layer !== anchorLayer && layer !== debugLayer);
    }



    /**
     * Gets a result layer by its name/ID.
     * @param {string} layerName - Name/ID of the layer
     * @returns {ol.layer.Vector|null} The vector layer, or null if not found
     */
    getLayerByName(layerName) {
        // Check if layer already exists
        if (this.resultLayers.has(layerName)) {
            return this.resultLayers.get(layerName);
        }
        return null;
    }









    // ==========================================
    // TILESET STUFF
    // ==========================================


    /**
     * Registers a tile layer configuration without immediately adding it to the map.
     * Used for lazy loading with the layer switcher control.
     * @param {string} url - Tile URL pattern with {z}/{x}/{y} placeholders
     * @param {Object} [options={}] - Tile layer options
     * @param {string} [options.name] - Display name for the layer
     * @returns {Object} The registered layer info object
     */
    registerTileLayer(url, options = {}) {
        const layerInfo = {
            url: url,
            options: options,
            name: options.name || `tile-layer-${this.tileSet.layers.length}`,
            layer: null // Will be created when activated
        };

        this.tileSet.layers.push(layerInfo);
        return layerInfo;
    }



    /**
     * Activates a specific tile set by index, hiding all others.
     * Creates the tile set if it hasn't been created yet (lazy loading).
     * @param {number} index - Index of the tile set to activate
     */
    activateTileSet(index) {
        if (index < 0 || index >= this.tileSet.layers.length) {
            console.error(`ERROR: Invalid tile layer index: ${index}`);
            return;
        }

        // Hide all tile layers
        this.tileSet.layers.forEach((layerInfo, i) => {
            if (layerInfo.layer) {
                layerInfo.layer.setVisible(i === index);
            }
        });

        // Create and show the selected layer if it doesnt exist yet
        const selectedLayer = this.tileSet.layers[index];
        if (!selectedLayer.layer) {
            selectedLayer.layer = this._createTileLayer(selectedLayer.url, selectedLayer.options);
            this.map.addLayer(selectedLayer.layer);
            // Move to bottom so other layers appear on top
            this.map.getLayers().remove(selectedLayer.layer);
            this.map.getLayers().insertAt(0, selectedLayer.layer);
        } else {
            selectedLayer.layer.setVisible(true);
        }

        this.tileSet.currentIndex = index;
    }






    // ==========================================
    // UI EVENT HANDLING
    // ==========================================


    /**
     * Handles user clicking on a map marker.
     * Extracts the uid from the feature and notifies MapApp.
     * @param {ol.Feature} feature - The clicked feature
     */
    userClicksOnMarker(feature) {
        const uid = feature.item.uid;

        if (uid && this.mapApp && typeof this.mapApp.userClicksOnMapMarkerItemByUId === 'function') {
            this.mapApp.userClicksOnMapMarkerItemByUId(uid);
        }
    }

    /**
     * Handles user clicking on an empty area of the map (no marker).
     * Notifies MapApp to perform a reverse search at the clicked location.
     * @param {Array<number>} coordinate - The map coordinate [x, y] in Web Mercator (EPSG:3857)
     */
    userClicksOnNonMarkerPointOnMap(coordinate) {
        this.mapApp.userClicksOnNonMarkerPointOnMap(coordinate);
    }



    /**
     * Handles user hovering over a map feature.
     * Shows or hides the popup tooltip based on hover state.
     * @param {ol.Feature|null} feature - The feature being hovered over, or null if none
     * @param {Array<number>} coordinate - The map coordinate [x, y] for popup positioning
     */
    userHoversOverFeature(feature, coordinate) {
        if (feature && feature !== this.currentHoverFeature) {
            // Show popup with dName
            const hoverTextHtml = calculateHoverTextHtml(feature.item);

            if (hoverTextHtml) {
                this.popupState.element.innerHTML = hoverTextHtml;
                this.popupState.element.style.display = 'block';
                this.popupState.overlay.setPosition(coordinate);
                this.currentHoverFeature = feature;
            }
        } else if (!feature && this.currentHoverFeature) {
            // Hide popup when not hovering over a feature
            this.popupState.element.style.display = 'none';
            this.currentHoverFeature = null;
        }
    }





    

    // ==========================================
    // UI HELPERS
    // ==========================================






    /**
     * Handles visibility change from the overlay control.
     * Updates result layer visibility and notifies MapApp.
     * @param {string|null} layerId - The layer id that changed, or null for toggle all
     * @param {boolean} visible - The new visibility state
     * @param {Object} allState - Current visibility state for all layers
     * @private
     */
    handleOverlayVisibilityChange(layerId, visible, allState) {
        if (layerId === null) {
            // Toggle all - update all result layers
            for (const [id, isVisible] of Object.entries(allState)) {
                this.setResultLayerVisibility(id, isVisible);
            }
        } else {
            // Update single result layer
            this.setResultLayerVisibility(layerId, visible);
        }

        // Notify MapApp of visibility change for state persistence
        if (this.mapApp && this.mapApp.onOverlayVisibilityChange) {
            this.mapApp.onOverlayVisibilityChange(layerId, visible, allState);
        }
    }









    // ==========================================
    // Marker/Pin Styles
    // ==========================================




    /**
     * Creates a dynamic style function for map pins that uses caching.
     * @returns {Function} Style function (feature, resolution) => ol.style.Style
     */
    makePinStyleFunction() {
        return (feature, resolution) => {
            return this.computeCachedStyle(feature, resolution);
        };
    }

    /**
     * Returns a cached style for a feature, creating one if needed.
     * Caches based on feature type (ptype), subtype, and zoom level.
     * @param {ol.Feature} feature - The feature to style
     * @param {number} resolution - Current map resolution
     * @returns {ol.style.Style} The style for the feature
     */
    computeCachedStyle(feature, resolution) {
        const item = feature.item;

        // calculate hash
        // IMPORTANT!!! This needs to take into account any details that might make the style change
        // so anything handled below differently, like isBookmarked, etc.
        const hashid = this._calcStyleCacheKey(item, resolution);

        // Return cached style if available
        if (this.styleCache[hashid]) {
            return this.styleCache[hashid];
        }

        // we will have to create a new style
        const ptype = item.ptype;
        const isCustomized = item.isCustomized;
        const userLook = item.userLook;
        const isBookmarked = item[FIELD_ISBOOKMARK];

        // Build new style
        let newStyle;
        if (isCustomized && userLook) {
            // create new custom style
            newStyle = this._createCustomPinStyle(item, resolution);
        } else if (ptype === PTYPES.PERSON) {
            newStyle = this._createPersonPinStyle(item, isBookmarked);
        } else if (ptype === PTYPES.ACTION) {
            newStyle = this._createActionPinStyle(item, isBookmarked);
        } else {
            // default yellow style
            newStyle = this._createYellowPinStyle(item, isBookmarked, resolution);
        }

        // Cache and return
        this.styleCache[hashid] = newStyle;
        return newStyle;
    }

    /**
     * Generates a cache key for style lookups based on item properties and zoom.
     * @param {Object} item - The POI data item attached to the feature
     * @param {number} resolution - Current map resolution
     * @returns {string} Cache key string
     * @private
     */
    _calcStyleCacheKey(item, resolution) {
        // ATTN: Redo this function to make it more general purpose

        const ptype = item.ptype;
        const isCustomized = item.isCustomized;
        const userLook = item.userLook;
        const isBookmarked = item[FIELD_ISBOOKMARK];

        // pin sizes are a function of this whole zoom level alone (see _calcScaleZoomLevel)
        const zoomBucket = this._calcScaleZoomLevel(resolution);

        let hashStr;
        if (isCustomized && userLook) {
            // compute a hash that is unique for every user
            const userHash = this._createStyleHashForUserLookItem(item, zoomBucket);
            hashStr = userHash;
        } else if (ptype === PTYPES.PERSON) {
            // note person style cache is not dependent on scale
            const subType = item.htype;
            hashStr = `${ptype}-${subType}_${isBookmarked}`;
        } else {
            // yellow pins are scaled
            const subType = item.pcatg;
            hashStr = `${zoomBucket}-${ptype}-${subType}_${isBookmarked}`;
        }
        //
        return hashStr;
    }


    /**
     * Creates a hash string representing an item's visual style properties.
     * Used for cache invalidation when an item's appearance changes.
     * @param {Object} item - The item to hash
     * @param {string} item.ptype - Point type
     * @param {string} [item.userColor] - Custom color
     * @param {string} [item.userPin] - Custom pin icon
     * @param {string} [item.userStyle] - Custom style
     * @param {string} [item.userScale] - Custom scale
     * @param {string} [item.userLook] - Custom look
     * @param {boolean} [item.userCallout] - Whether callout label is shown
     * @param {string} [item.userLabel] - Custom label text
     * @param {string} [item.dName] - Display name (used as callout fallback)
     * @returns {string} Hex hash string representing the visual style
     */
    _createStyleHashForUserLookItem(item, scale) {
        // see CUSTOM_ITEM_FIELDS_VISUAL
        // idea is to return a str identifier that changes when one of the style elements change

        const calloutLabel =  item.userCallout ? (item.userLabel || item.dName) : '';
        const userPinPath = this.calcItemPinPath(item);

        // a short hash
        const hashText = `${item.ptype}|${item.userColor}|${userPinPath}|${item.userStyle}|${calloutLabel}|${item.userScale}|${item.userLook}|${scale}`;
        const hashStr = jrhash(hashText);
        // console.log(`hashkey: ${hashKey} and hashStr: ${hashStr}`);
        return hashStr;
    }



    /**
     * Creates a circle style for person markers with color based on housing type.
     * @param {Object} item - The POI data item for the person
     * @returns {ol.style.Style} Circle style with fill and stroke
     * @private
     */
    _createPersonPinStyle(item, isBookmarked) {
        const subType = item.htype;
        let color = this.calcMapPersonColor(subType);
        const personStrokeColor = MAP_PERSON_COLORS.stroke;
        const radius = isBookmarked ? MAP_CONSTANTS.PERSON_RADIUS_BOOKMARKED : MAP_CONSTANTS.PERSON_RADIUS_NORMAL;

        if (isBookmarked) {
            color = MAP_CONSTANTS.BOOKMARK_PERSON_COLOR;
        }

        return new ol.style.Style({
            zIndex: MAP_CONSTANTS.ZINDEX_BUMP_PERSON,
            image: new ol.style.Circle({
                radius: radius,
                fill: new ol.style.Fill({
                    color: color
                }),
                stroke: new ol.style.Stroke({
                    color: personStrokeColor,
                    width: 1
                })
            })
        });
    }

    calcMapPersonColor(subType) {
        if (this.customConfig && this.customConfig['personColors']) {
            return this.customConfig['personColors'][subType] || this.customConfig['personColors']['default'];
        }
        return MAP_PERSON_COLORS[subType] || MAP_PERSON_COLORS.default;
    }


    /**
     * Creates a diamond shape style for action markers with color based on action category.
     * @param {Object} item - The POI data item for the action
     * @returns {ol.style.Style} RegularShape style with fill and stroke
     * @private
     */
    _createActionPinStyle(item, isBookmarked) {
        const subType = item.pcat;
        let featureColor = MAP_ACTION_COLORS[subType] || MAP_ACTION_COLORS.default;
        const featureStrokeColor = MAP_ACTION_COLORS.stroke;

        if (isBookmarked) {
            featureColor = MAP_CONSTANTS.BOOKMARK_ACTION_COLOR;
        }

        return new ol.style.Style({
            zIndex: MAP_CONSTANTS.ZINDEX_BUMP_ACTION,
            image: new ol.style.RegularShape({
                points: 4,
                radius: 6,
                angle: Math.PI / 4,
                fill: new ol.style.Fill({
                    color: featureColor
                }),
                stroke: new ol.style.Stroke({
                    color: featureStrokeColor,
                    width: 1
                })
            })
        });
    }




    /**
     * Creates an icon style for map pin markers with zoom-dependent scaling.
     * @param {number} resolution - Current map resolution
     * @returns {ol.style.Style} Icon style with scaled map pin
     * @private
     */
    _createYellowPinStyle(item, isBookmarked, resolution) {
        return this.makeStyleFromConfig('yellow', item, isBookmarked, resolution);
    }

    /**
     * Creates an icon style from the style configuration for a given category and resolution.
     * Applies zoom-dependent scaling based on the style's scale parameters.
     * @param {string} category - The style category key (e.g., 'yellow')
     * @param {number} resolution - Current map resolution for zoom-dependent scaling
     * @returns {ol.style.Style} Icon style with scaled and colored pin
     */
    makeStyleFromConfig(category, item, isBookmarked, resolution) {
        // testing
        const optionUsePcatIconForBookmarkedYellowMarkers = MAP_CONSTANTS.USE_PCAT_IMAGE_FOR_BOOKMARKED_YELLOW_PINS;

        // style data
        const styleData = this.getStyleDataByCategory(category);
        //
        let {image, color, anchor, scaleParams} = styleData;
        let scale = this._calcZoomDependentScale(resolution, scaleParams);
        //
        // test bookmarked default pins being colored
        let flagBoxFeature = false;
        if (isBookmarked) {
            if (optionUsePcatIconForBookmarkedYellowMarkers) {
                // let's try using the pcatg image
                flagBoxFeature = true;
                image = this.lookupMarkerPcatgImageForItem(item);
                scale = scale * 7;
                color = MAP_CONSTANTS.BOOKMARK_PCATG_ICON_COLOR;
            } else {
                color = MAP_CONSTANTS.BOOKMARK_PIN_COLOR;
                scale = scale * MAP_CONSTANTS.BOOKMARK_PIN_SCALING;
            }
        }
        //
        const style = new ol.style.Style({
            zIndex: MAP_CONSTANTS.ZINDEX_BUMP_YELLOW,
            image: new ol.style.Icon({
                src: this.resolveStaticPath(image),
                anchor: anchor,
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                scale: scale * this.getCustomConfigValueOrDefault('mapMarkerMapScale', MAPAPP_DEFAULTS.MAP_MARKER_SCALE),
                color: color,
                //width: MAP_CONSTANTS.CUSTOM_MARKER_FIXED_WIDTH,
                //height: MAP_CONSTANTS.CUSTOM_MARKER_FIXED_HEIGHT,
            })
        });
        return style;
    }





    /**
     * Calculates a zoom-dependent scale value from resolution and scale parameters.
     * @param {number} resolution - Current map resolution
     * @param {Object} scaleParams - Scale parameters with MIN, MAX, BASE_MULTIPLIER, ZOOM_FACTOR
     * @returns {number} Clamped scale value
     * @private
     */
    _calcZoomDependentScale(resolution, scaleParams) {
        const {MIN, MAX, BASE_MULTIPLIER, ZOOM_FACTOR} = scaleParams;
        const zoomScale = this._calcScaleZoomLevel(resolution);
        let scale = BASE_MULTIPLIER * Math.pow(ZOOM_FACTOR, zoomScale);
        scale = Math.min(scale, MAX);
        scale = Math.max(scale, MIN);
        return scale;
    }


    /**
     * The whole zoom level a pin's size is computed from (_calcZoomDependentScale), and so also the
     * zoom part of the style cache key (_calcStyleCacheKey): the two MUST be this one value, or one
     * cached style spans two pin sizes and whichever was drawn first wins for both (it used to be a
     * different rounding, which drew pins 25% too big or too small depending on where one first zoomed).
     * @param {number} resolution - Current map resolution
     * @returns {number} The rounded zoom level
     * @private
     */
    _calcScaleZoomLevel(resolution) {
        return Math.round(Math.log2(MAP_CONSTANTS.MAX_RESOLUTION / resolution));
    }

    /**
     * Creates a custom pin style for bookmarked/customized POI items.
     * Supports user-defined color, pin image, scale, and optional callout label.
     * @param {Object} item - The POI data item with userLook customization properties
     * @param {number} resolution - Current map resolution
     * @returns {ol.style.Style} Custom icon style with optional text label
     * @private
     */
    _createCustomPinStyle(item, resolution) {
        // (a color(srgb ...) value Safari's old alpha colour picker stored is drawn near-black by
        // OpenLayers: repaired to #rrggbb here, so pins saved before the fix show their colour)
        const color = normalizeCssColorToHex(item.userColor) || MAP_CUSTOMS.COLOR_DEFAULT;
        const calloutLabel =  item.userCallout ? (item.userLabel || item.dName) : '';
        const userScaleFactor = item.userScale ? (item.userScale/100.0) : 1.0;
        let pinImagePath = this.calcItemPinPath(item);
        
        // zoom-dependent scaling using shared scale params, with user scale on top
        let scale = this._calcZoomDependentScale(resolution, MAP_CONSTANTS.PIN_SCALE);
        scale *= MAP_CUSTOMS.SCALE_DEFAULT * userScaleFactor * MAP_CONSTANTS.CUSTOM_MARKER_BASE_SCALE;

        const styleOptions = {
            image: new ol.style.Icon({
                zIndex: MAP_CONSTANTS.ZINDEX_BUMP_CUSTOM_BOOKMARK,
                src: this.resolveStaticPath(pinImagePath),
                //anchor: [0.5, 1],
                anchor: [0.5, 0.75],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                scale: scale * this.getCustomConfigValueOrDefault('mapMarkerMapScale', MAPAPP_DEFAULTS.MAP_MARKER_SCALE),
                color: color,
            })
        };

        if (calloutLabel) {
            styleOptions.text = new ol.style.Text({
                zIndex: MAP_CONSTANTS.ZINDEX_BUMP_CUSTOM_BOOKMARK+1,
                text: calloutLabel,
                font: '12px sans-serif',
                offsetY: 30 * scale,
                fill: new ol.style.Fill({ color: '#000' }),
                stroke: new ol.style.Stroke({ color: '#fff', width: 3 }),
                textAlign: 'center',
                scale: scale*2 * this.getCustomConfigValueOrDefault('mapMarkerMapScale', MAPAPP_DEFAULTS.MAP_MARKER_SCALE),
            });
        }

        return new ol.style.Style(styleOptions);
    }



    calcItemPinPath(item) {
        let pinImagePath = (item.userPin ? ('images/' + item.userPin) : null);
        if (!pinImagePath) {
            pinImagePath = this.mapApp.getImageHelper().calcUserImagePathForItem(item);
            //|| MAP_CUSTOMS.PIN_DEFAULT;
        }

        return pinImagePath;
    }













    // ==========================================
    // Result Layer Management
    // ==========================================


    /**
     * Creates a result layer with the given name and options.
     * Called during initialization to pre-create all needed layers.
     * @param {string} layerName - Name/ID of the layer
     * @param {Object} options - Layer creation options
     * @param {boolean} [options.visible=true] - Initial visibility
     * @param {number} [options.zIndex=MAP_LAYER_ZINDICES.DEFAULT_GENERIC] - Layer z-index
     * @param {Function|ol.style.Style|null} [options.style=null] - Layer style (defaults to pin style)
     * @param {boolean} [options.updateWhileAnimating=false] - Re-render every frame during zoom/kinetic animation
     * @param {boolean} [options.updateWhileInteracting=false] - Re-render every frame during a drag
     * @returns {ol.layer.Vector} The created vector layer
     */
    createResultLayer(layerName, options = {}) {
        if (this.resultLayers.has(layerName)) {
            console.warn(`Layer "${layerName}" already exists.`);
            return this.resultLayers.get(layerName);
        }

        const {
            visible = true,
            zIndex = MAP_LAYER_ZINDICES.DEFAULT_GENERIC,
            style = null,
            updateWhileAnimating = false,
            updateWhileInteracting = false
        } = options;

        const vectorSource = new ol.source.Vector({
            features: []
        });

        const vectorLayer = new ol.layer.Vector({
            source: vectorSource,
            style: style || this.makePinStyleFunction(),
            visible: visible,
            zIndex: zIndex,
            // Re-render this layer on every frame of a drag/zoom instead of translating a
            // cached canvas. OpenLayers' default (false) means the cached canvas only covers
            // the viewport as of the last real render plus renderBuffer (100px), so anything
            // panned in beyond that strip stays blank until the interaction ends -- barely
            // noticeable for small pin layers, glaring for a full-screen dim mask. Only worth
            // the per-frame cost for layers where that matters (see the region layer below).
            updateWhileAnimating: updateWhileAnimating,
            updateWhileInteracting: updateWhileInteracting
        });

        this.map.addLayer(vectorLayer);
        this.resultLayers.set(layerName, vectorLayer);

        return vectorLayer;
    }


    /**
     * Creates multiple result layers from an array of config objects.
     * Each config should have { layerName, options }.
     * @param {Array<Object>} layerConfigs - Array of { layerName, options } objects
     */
    createAllResultLayers(layerConfigs) {
        for (const config of layerConfigs) {
            this.createResultLayer(config.layerName, config.options);
        }
    }


    /**
     * Creates auxiliary result layers (highlight, anchor ring, anchor POI, region).
     * These layers have special styles defined here in the map manager.
     * Called during initialization after searcher result layers are created.
     */
    createAuxResultLayers() {
        // Highlight layer (gold ring around selected POI)
        this.createResultLayer(MAP_AUX_OVERLAY_LAYERS.HIGHLIGHT, {
            style: this._makeHighlightStyle(),
            zIndex: MAP_LAYER_ZINDICES.POI_HIGHLIGHT
        });

        // Anchor ring layer (blue ring around anchored POI)
        this.createResultLayer(MAP_AUX_OVERLAY_LAYERS.ANCHOR, {
            style: this._makeAnchorRingStyle(),
            zIndex: MAP_LAYER_ZINDICES.ANCHOR
        });

        // Anchor POI layer (actual marker for anchored POI)
        this.createResultLayer(MAP_AUX_OVERLAY_LAYERS.ANCHOR_POI, {
            style: this.makePinStyleFunction(),
            zIndex: MAP_LAYER_ZINDICES.ANCHOR
        });

        // Region outline/mask layer (style is set dynamically by updateRegionLayerStyle)
        // updateWhileAnimating/Interacting: this layer holds just two features (the dim mask
        // and the border), so re-rendering it every frame is cheap -- and without it the dim
        // mask and outline are missing from map area panned in during a drag until mouse-up.
        this.createResultLayer(MAP_AUX_OVERLAY_LAYERS.REGION, {
            style: this._makeRegionOutlineStyle(),
            zIndex: MAP_LAYER_ZINDICES.REGIONS,
            updateWhileAnimating: true,
            updateWhileInteracting: true
        });
    }


    /**
     * Creates the highlight ring style (gold semi-transparent circle).
     * @returns {ol.style.Style} The highlight style
     * @private
     */
    _makeHighlightStyle() {
        return new ol.style.Style({
            image: new ol.style.Circle({
                radius: 14,
                fill: new ol.style.Fill({
                    color: this.getCustomConfigValueOrDefault('mapHighlightColor1', MAPAPP_DEFAULTS.MAP_HIGHLIGHT_COLOR_1),
                }),
                stroke: new ol.style.Stroke({
                    color: this.getCustomConfigValueOrDefault('mapHighlightColor2', MAPAPP_DEFAULTS.MAP_HIGHLIGHT_COLOR_2),
                    width: 3
                })
            })
        });
    }


    /**
     * Creates the anchor ring style (blue semi-transparent circle).
     * @returns {ol.style.Style} The anchor ring style
     * @private
     */
    _makeAnchorRingStyle() {
        return new ol.style.Style({
            image: new ol.style.Circle({
                radius: 22,
                fill: new ol.style.Fill({
                    color: this.getCustomConfigValueOrDefault('mapAnchorColor1', MAPAPP_DEFAULTS.MAP_ANCHOR_COLOR_1),
                }),
                stroke: new ol.style.Stroke({
                    color: this.getCustomConfigValueOrDefault('mapAnchorColor2', MAPAPP_DEFAULTS.MAP_ANCHOR_COLOR_2),
                    width: 3
                })
            })
        });
    }


    /**
     * Gets an existing result layer by name.
     * Unlike the former getOrCreateResultLayer, this does NOT create layers —
     * all layers should be pre-created during initialization.
     * @param {string} layerName - Name/ID of the layer
     * @returns {ol.layer.Vector|null} The vector layer, or null if not found
     */
    getResultLayer(layerName) {
        if (!this.resultLayers.has(layerName)) {
            console.error(`ERROR: Result layer "${layerName}" not found. Was it created during initialization?`);
            return null;
        }
        return this.resultLayers.get(layerName);
    }

































    // ==========================================
    // MAIN DRAWING
    // ==========================================


    /**
     * Update a pre-existing result layer with search results.
     * The layer must have been created during initialization via createResultLayer().
     * Clears existing features before adding new ones.
     * @param {string} layerName - Name/ID of the layer
     * @param {Array} results - Array of search result items with geometry
     */
    recreateAllMapFeatureMarkersForLayerWithNewResults(layerName, results) {
        if (DEBUG_REFRESH) {
            if (layerName != 'highlight') {
                console.log(`  cmb: recreateAllMapFeatureMarkersForLayerWithNewResults for layer ${layerName}.`)
            }
        }

        const layer = this.getResultLayer(layerName);

        const source = layer.getSource();

        // Clear existing features
        source.clear();

        // Convert results to OpenLayers features
        const features = this.createFeaturesFromResults(results);

        // Add features to source
        source.addFeatures(features);

        return layer;
    }




    /**
     * Convert search results to OpenLayers features
     * Similar to addGeoJsonLayer but works with raw result objects
     * @param {Array} results - Array of result items
     * @returns {Array<ol.Feature>} Array of OpenLayers features
     */
    createFeaturesFromResults(results) {

        const features = [];

        // loop all features and creat
        for (let i = 0; i < results.length; i++) {
            const item = results[i];

            // Skip items without geometry
            if (!item.geometry || !item.geometry.coordinates) {
                continue;
            }

            // Extract coordinates
            const coords = item.geometry.coordinates;

            // Coordinates are already in Web Mercator (EPSG:3857)
            const coordinate = [coords[0], coords[1]];

            // ATTN: TODO for speed and memory we could just create this object manually with an object creation = {}
            // Create feature with geometry
            const feature = new ol.Feature({
                geometry: new ol.geom.Point(coordinate),
            });

            // Store reference to the source item on the feature
            feature.item = item;

            features.push(feature);
        }
        return features;
    }

























    
    /**
     * Clear a result layer (removes features but keeps the layer)
     * @param {string} layerName - Name/ID of the layer
     */
    clearResultLayer(layerName) {
        if (this.resultLayers.has(layerName)) {
            const layer = this.resultLayers.get(layerName);
            layer.getSource().clear();
        }
    }












    // ==========================================
    // AUTO EXTENT STUFF
    // ==========================================


    /**
     * Determines if a layer should be included when computing the combined visible extent.
     * Excludes hidden layers and non-interactive layers (highlight, anchor, region).
     * @param {ol.layer.Vector} layer - The layer to check
     * @param {string} layerName - Name/ID of the layer
     * @returns {boolean} True if the layer should be included in extent calculations
     */
    includeLayerInExtenting(layer, layerName) {
        if (!layer.getVisible()) {
            return false;
        }
        if (!this.isInteractiveLayer(layer)) {
            return false;
        }
        return true;
    }


    /**
     * Compute the combined extent of all visible features across all visible result layers.
     * @returns {ol.extent|null} The combined extent, or null if no visible features
     */
    computeAllVisibleFeaturesExtent() {
        const combinedExtent = ol.extent.createEmpty();
        //console.log(`DEBUG: IN computeAllVisibleFeaturesExtent, initial extent = "${JSON.stringify(combinedExtent)}".`);

        for (const [layerName, layer] of this.resultLayers) {
            // Skip non-visible layers
            if (!this.includeLayerInExtenting(layer, layerName)) {
                continue;
            }

            // Get extent from this layer's source
            //console.log(`DEBUG: Incorporating visual layer "${layerName}".`);
            const source = layer.getSource();
            if (source) {
                const layerExtent = source.getExtent();
                if (layerExtent && !ol.extent.isEmpty(layerExtent)) {
                    //console.log(`DEBUG:   extending to layer extent "${JSON.stringify(layerExtent)}".`);
                    ol.extent.extend(combinedExtent, layerExtent);
                }
            }
        }

        return ol.extent.isEmpty(combinedExtent) ? null : combinedExtent;
    }


    /**
     * Compute the combined extent of the visible features (across all visible result layers) that lie inside a geometry.
     * Used to fit the view to the markers within a selected neighborhood/region.
     * Points are tested for real containment in the geometry; other feature geometries are kept if they touch it.
     * @param {ol.geom.Geometry} olGeometry - The containing geometry (e.g. a region Polygon/MultiPolygon), in map projection
     * @returns {ol.extent|null} The combined extent, or null if no visible features fall inside
     */
    computeVisibleFeaturesExtentWithinGeometry(olGeometry) {
        const combinedExtent = ol.extent.createEmpty();

        for (const [layerName, layer] of this.resultLayers) {
            // Skip non-visible layers
            if (!this.includeLayerInExtenting(layer, layerName)) {
                continue;
            }

            const source = layer.getSource();
            if (!source) {
                continue;
            }
            for (const feature of source.getFeatures()) {
                const geom = feature.getGeometry();
                if (!geom) {
                    continue;
                }
                let isInside;
                if (geom.getType() === 'Point') {
                    isInside = olGeometry.intersectsCoordinate(geom.getCoordinates());
                } else {
                    isInside = olGeometry.intersectsExtent(geom.getExtent());
                }
                if (isInside) {
                    ol.extent.extend(combinedExtent, geom.getExtent());
                }
            }
        }

        return ol.extent.isEmpty(combinedExtent) ? null : combinedExtent;
    }





    /**
     * Compute the combined extent of all visible features across all visible result layers.
     * @returns {ol.extent|null} The combined extent, or null if no visible features
     */
    computeResultListFeaturesExtent(results) {
        const combinedExtent = ol.extent.createEmpty();

        for (const result of results) {
            const geom = result.geometry;
            if (!geom || !geom.coordinates) {
                continue;
            }
            ol.extent.extendCoordinate(combinedExtent, geom.coordinates);
        }

        return ol.extent.isEmpty(combinedExtent) ? null : combinedExtent;
    }


    /**
     * Calculate the extent of the visible region overlay layer.
     * Looks for the 'border' feature specifically to avoid the inverted mask extent.
     * @returns {ol.extent|null} The region extent, or null if no visible region
     */
    calcVisibleRegionOverlayExtent() {
        const regionLayer = this.getLayerByName(MAP_AUX_OVERLAY_LAYERS.REGION);
        if (regionLayer && regionLayer.getVisible()) {
            const source = regionLayer.getSource();
            if (source) {
                // Look for the border feature (the actual region polygon)
                // The mask feature covers the entire map extent and isn't useful for fitting
                const features = source.getFeatures();
                for (const feature of features) {
                    if (feature.get('featureType') === 'border') {
                        const geometry = feature.getGeometry();
                        if (geometry) {
                            const extent = geometry.getExtent();
                            if (extent && !ol.extent.isEmpty(extent)) {
                                return extent;
                            }
                        }
                    }
                }
                // Fallback: if no border feature found (non-inverted style), use source extent
                const regionExtent = source.getExtent();
                if (regionExtent && !ol.extent.isEmpty(regionExtent)) {
                    return regionExtent;
                }
            }
        }
        return null;
    }


    /**
     * Auto zoom to fit all visible features across all visible result layers.
     * @param {string} autoZoomStyle - The zoom style: 'ensureVisible' or 'zoomIn'
     */
    autoZoomAllVisibleFeatureExtents(autoZoomStyle) {
        if (!autoZoomStyle) {
            return;
        }
        let extent = this.computeAllVisibleFeaturesExtent();
        if (!extent) {
            // no visible features extent -- check for a selected region
            extent = this.calcVisibleRegionOverlayExtent();
            // if still no extent, reset to initial bounds
            if (!extent) {
                this.mapApp.fitBoundsInitial();
                return;
            }
        }
        //console.log(`FItting to extent: "${JSON.stringify(extent)}".`)
        this.fitToExtentWithStyle(extent, autoZoomStyle, null);
    }



        /**
     * Auto zoom to fit all visible features across all visible result layers.
        * @param {array} results - list of result items with uids to match to features'
        * @param {string} autoZoomStyle - The zoom style: 'ensureVisible' or 'zoomIn'
     */
    autoZoomPanToResultSet(results, autoZoomStyle) {
        if (!autoZoomStyle) {
            return;
        }
        let extent = this.computeResultListFeaturesExtent(results);
        if (!extent) {
            // no visible features extent -- check for a selected region
            extent = this.calcVisibleRegionOverlayExtent();
            // if still no extent, reset to initial bounds
            if (!extent) {
                this.mapApp.fitBoundsInitial();
                return;
            }
        }
        //console.log(`FItting to extent: "${JSON.stringify(extent)}".`)

        // flash point doesnt work since highlight region not visible; until we make flashpoint more fleixble we cant use it
        //const flashPoint = results.length>0 ? {uid:results[0].uid} : null;
        //console.log(`flashPoint: "${JSON.stringify(flashPoint)}".`)
        const flashPoint = null;

        this.fitToExtentWithStyle(extent, autoZoomStyle, flashPoint);
    }






    /**
     * Fit map view to an extent using the specified zoom style.
     * ATTN: there is some real overlap and DRY violations between this code and the code in zoomExtentToPoiUid()
     * @param {Array<number>} extent - The extent to fit to [minX, minY, maxX, maxY] in Web Mercator
     * @param {string} autoZoomStyle - The zoom style: 'ensureVisible', 'zoomIn', 'expand', or 'panExpand'
     * @param {string|null} flashPoint - UID of the feature to flash after fitting, or null for no flash
     * @returns {boolean} True if the map view was changed, false if extent was already visible
     */
    fitToExtentWithStyle(extent, autoZoomStyle, flashPoint) {
        // 
        //console.log('ATTN: IN fitToExtentWithStyle.');
        const avoidRotationBug = true;

        //
        const padding = MAP_CONSTANTS.RESULT_LAYER_PADDING;
        const view = this.map.getView();
        const viewExtent = this.calcCurrentViewExtent();
        // const targetExtent = this.calcShrunkenExtent(extent, padding, -1 * resolution);
        const targetExtent = extent;

        let isContained;
        //
        if (avoidRotationBug) {
            isContained = this.isExtentVisible(extent);
        } else {
            // Shrink the view extent by padding to get the "safe" visible area
            const view = this.map.getView();
            const resolution = view.getResolution();
            const shrunkenViewExtent = this.calcShrunkenExtent(viewExtent, padding, resolution);
            // Check if extent is already visible within the padded view
            isContained = this.doesExtentAFullyContainExtentB(shrunkenViewExtent, targetExtent);
        }

        // ATTN: temporarily disabling this
        if (isContained && (autoZoomStyle != AUTO_ZOOM_STYLE.ZOOM_IN)) {
            // nothing to do, we just wanted all visible and they are
            // console.log(`EXTENT ALREADY IN VIEW nothing to do currentExtent = "${JSON.stringify(viewExtent)}"  target = "${targetExtent}".`)
            if (flashPoint) { this.flashFeatureByUid(this.getLayerByName('highlight'), flashPoint); }
            return false;
        }


        let maxZoom, duration;
        let newExtent;
        //
        if (autoZoomStyle === AUTO_ZOOM_STYLE.EXPAND) {
            // Extend the current view to include the new extent; never zoom in
            // Use the full viewExtent (not padded) so we maintain current view plus new point
            // we could conceivably 
            newExtent = ol.extent.clone(viewExtent);
            ol.extent.extend(newExtent, targetExtent);
            maxZoom = view.getZoom(); // Don't zoom in, only out
            duration = MAP_CONSTANTS.ANIMATION_DURATION_EXPAND;
        } else if (autoZoomStyle === AUTO_ZOOM_STYLE.ZOOM_IN) {
            // try to zoom in a lot to get a tight fit (dont worry about current view)
            newExtent = ol.extent.clone(targetExtent);
            maxZoom = this.getCustomConfigValueOrDefault('maxZoomInToExtentScale', MAP_CONSTANTS.MAX_ZOOMIN_TO_FIT_SCALE);
            duration = MAP_CONSTANTS.ANIMATION_DURATION_ZOOMIN;
        } else if (autoZoomStyle === AUTO_ZOOM_STYLE.PAN_EXPAND) {
            // dont go any CLOSER than we already are but go to the new extent (dont worry about current view)
            newExtent = ol.extent.clone(targetExtent);
            maxZoom = view.getZoom();
            duration = MAP_CONSTANTS.ANIMATION_DURATION_PAN_EXPAND;
        } else {
            throw new Error(`ERROR: Uknown autoZoomStyle = "${autoZoomStyle}".`);
        }

        view.fit(newExtent, {
            padding: padding,
            maxZoom: maxZoom,
            duration: duration,
            callback: () => { if (flashPoint) this.flashFeatureByUid(this.getLayerByName('highlight'), flashPoint);},
        });

        return true;
    }



    /**
     * Checks if extentA fully contains extentB using axis-aligned bounding box comparison.
     * Note: Does NOT work correctly if the map is rotated.
     * @param {Array<number>} extentA - The containing extent [minX, minY, maxX, maxY]
     * @param {Array<number>} extentB - The extent to test for containment [minX, minY, maxX, maxY]
     * @returns {boolean} True if extentA fully contains extentB
     */
    doesExtentAFullyContainExtentB(extentA, extentB) {
        // built in way, DOES NOT WORK IF MAP IS ROTATED!!!
        const isContained = ol.extent.containsExtent(extentA, extentB);
        return isContained;
    }





    // HELPERS

    /**
     * Check if a coordinate is visible in the current view (works with rotation)
     * @param {Array} coordinate - [x, y] in map projection
     * @returns {boolean}
     */
    isCoordinateVisible(coordinate) {
        const pixel = this.map.getPixelFromCoordinate(coordinate);
        if (!pixel) return false;
        
        const size = this.map.getSize();
        const [x, y] = pixel;
        
        return x >= 0 && x <= size[0] && y >= 0 && y <= size[1];
    }


    /**
     * Check if an extent is fully visible (works with rotation)
     * @param {Array} extent - [minX, minY, maxX, maxY]
     * @returns {boolean}
     */
    isExtentVisible(extent) {
        // Check all four corners of the extent
        const corners = [
            [extent[0], extent[1]], // bottom-left
            [extent[0], extent[3]], // top-left
            [extent[2], extent[1]], // bottom-right
            [extent[2], extent[3]], // top-right
        ];
        
        return corners.every(coord => this.isCoordinateVisible(coord));
    }







    /**
     * Shrinks an extent inward by a padding amount scaled by a multiplier.
     * Used to determine the "safe" visible area within the view minus padding.
     * @param {Array<number>} extent - The extent to shrink [minX, minY, maxX, maxY]
     * @param {Array<number>} padding - Padding in pixels [top, right, bottom, left]
     * @param {number} multiplier - Scale factor for padding (typically map resolution)
     * @returns {Array<number>} The shrunken extent [minX, minY, maxX, maxY]
     */
    calcShrunkenExtent(extent, padding, multiplier) {
        const paddedViewExtent = [
            extent[0] + (padding[3] * multiplier),
            extent[1] + (padding[2] * multiplier),
            extent[2] - (padding[1] * multiplier),
            extent[3] - (padding[0] * multiplier)
        ];
        return paddedViewExtent;
    }


    /**
     * Zoom the map to center on a specific POI item.
     * ATTN: there is some real overlap and DRY violations between this code and the code in fitToExtentWithStyle()
     * @param {string} uid - The unique identifier of the POI
     * @param {Object} item - The POI item with geometry.coordinates in EPSG:3857
     * @param {string} autoZoomStyle - The zoom style: 'ensureVisible', 'zoomIn', 'expand', or 'panExpand'
     * @param {boolean} flagFlashMarker - If true, flash the highlight marker after zooming
     * @returns {boolean} True if the map view was changed, false if item had no geometry
     */
    zoomExtentToPoiUid(uid, item, autoZoomStyle, flagFlashMarker) {
        if (!item || !item.geometry || !item.geometry.coordinates) {
            return false;
        }

        //console.log(`ATTN: DEBUG in zoomExtentToPoiUid ${uid}.`);

        // Coordinates are already in Web Mercator (EPSG:3857)
        const coords = item.geometry.coordinates;
        // Create a point extent from the single coordinate
        const extent = [coords[0], coords[1], coords[0], coords[1]];        

        const flashPoint = flagFlashMarker ? uid : null;

        // now hand off to fitToExtentWithStyle
        return this.fitToExtentWithStyle(extent, autoZoomStyle, flashPoint);
    }




    /**
     * Calculates the current map view extent in map projection coordinates.
     * @returns {Array<number>} The current view extent [minX, minY, maxX, maxY]
     */
    calcCurrentViewExtent() {
        const view = this.map.getView();
        const viewExtent = view.calculateExtent(this.map.getSize());

        // Compare these values
        /*
        console.log(`map.getSize(): ${JSON.stringify(this.map.getSize())}`);
        console.log(`container rect: ${JSON.stringify(this.map.getTargetElement().getBoundingClientRect())}`);
        console.log(`devicePixelRatio: ${JSON.stringify(window.devicePixelRatio)}`);
        console.log(`view center: ${JSON.stringify(this.map.getView().getCenter())}`);
        console.log(`view resolution: ${JSON.stringify( this.map.getView().getResolution())}`);
        console.log('view rotation:', this.map.getView().getRotation());
        console.log('map pixelRatio:', this.map.pixelRatio_);
        */
        return viewExtent;
    }













    // ==========================================
    // Result Layer Looks
    // ==========================================




    /**
     * Show/hide a result layer
     * @param {string} layerName - Name/ID of the layer
     * @param {boolean} visible - Whether to show the layer
     */
    setResultLayerVisibility(layerName, visible) {
        if (this.resultLayers.has(layerName)) {
            const layer = this.resultLayers.get(layerName);
            layer.setVisible(visible);
        }
    }

    /**
     * Update visibility and opacity of each of the result layers.
     * The current tab's layer is shown at 100% opacity.
     * Other tabs' layers are shown at reduced opacity if their overlay setting is enabled,
     * or hidden if their overlay setting is disabled.
     */
    updateResultLayersVisibility() {
        // Get current active tab name from MapApp
        const activeTabName = this.mapApp ? this.mapApp.calcActiveSearcherName() : null;

        // Get overlay visibility state from the OverlayControl
        const overlayState = this.controls.overlayControl
            ? this.controls.overlayControl.getVisibilityState()
            : {};

        // Layer names that have corresponding result layers
        const layerNames = [SEARCH_TYPES.WHITE, SEARCH_TYPES.YELLOW, SEARCH_TYPES.REVERSE, SEARCH_TYPES.ADVANCED, SEARCH_TYPES.BOOKMARKS, SEARCH_TYPES.HISTORY, SEARCH_TYPES.SAVEDSEARCH];

        const inactiveLayerOpacity = this.mapApp.getStateManager().getInactiveLayerOpacity();

        for (const layerName of layerNames) {
            const layer = this.resultLayers.get(layerName);
            if (!layer) {
                continue;
            }

            if (layerName === activeTabName) {
                // Current tab: show at full opacity
                layer.setVisible(true);
                layer.setOpacity(1.0);
            } else {
                // Other tabs: check overlay state
                const isOverlayEnabled = overlayState[layerName] ?? false;
                if (isOverlayEnabled) {
                    layer.setVisible(true);
                    layer.setOpacity(inactiveLayerOpacity);
                } else {
                    layer.setVisible(false);
                }
            }
        }
    }


// 



    // ==========================================
    // HIGHLIGHTED / ANCHOR SPECIAL MARKER display
    // ==========================================


    /**
     * Highlights a POI marker on the map with a visual ring indicator.
     * If item is null, clears the current highlight marker.
     * NOTE: we might cache the last value set and avoid doing this if its already same item highlighted as already IFF this turns out to be an expensive op
     * @param {string} uid - Unique identifier of the POI
     * @param {Object|null} item - The POI item with geometry data, or null to clear
     */
    highlightPoiByUid(uid, item) {
        // Clear highlight if no item provided
        const layerName = MAP_AUX_OVERLAY_LAYERS.HIGHLIGHT;
        //
        if (!item || !item.geometry || !item.geometry.coordinates) {
            this.clearResultLayer(layerName);
            return;
        }

        // Style and zIndex are set during layer creation in createAuxResultLayers()
        const layer = this.recreateAllMapFeatureMarkersForLayerWithNewResults(layerName, [item]);

        // remember layerHighlight for fast checking later?
        this.layerHighlight = layer;
    }




    /**
     * Finds a feature by uid on a layer and triggers a flash animation on it.
     * @param {ol.layer.Vector} layer - The layer containing the feature
     * @param {string} uid - The unique identifier of the feature to flash
     */
    flashFeatureByUid(layer, uid) {
        // Find the feature with matching uid on the layer
        const source = layer.getSource();
        const features = source.getFeatures();
        let targetFeature = null;
        for (const f of features) {
            if (f.item.uid === uid) {
                targetFeature = f;
                break;
            }
        }
        if (!targetFeature) {
            return;
        }

        const flashGeometry = targetFeature.getGeometry().clone();
        if (!this.isCoordinateVisible(flashGeometry.getCoordinates())) {
            // not in view yet we should delay the flash animation
            // delay it? if so then delay by MAP_CONSTANTS.ANIMATION_DURATION_PAN_EXPAND
        }
        // flash it
        this.doFlashFeatureByGeometry(layer, flashGeometry)
    }



    /**
     * Animates an expanding, fading circle flash effect at a geometry location.
     * Uses vectorContext for correct rendering with map rotation and DPI scaling.
     * @param {ol.layer.Vector} layer - The layer to render the flash animation on
     * @param {ol.geom.Geometry} flashGeometry - The geometry to flash at (typically a cloned Point)
     */
    doFlashFeatureByGeometry(layer, flashGeometry) {
        // Animate an expanding, fading circle using vectorContext
        // vectorContext handles all view transforms (rotation, DPI) correctly

        const duration = 1200;
        const start = Date.now();

        const listenerKey = layer.on('postrender', (event) => {
            const elapsed = Date.now() - start;
            const t = elapsed / duration;

            if (t >= 1) {
                ol.Observable.unByKey(listenerKey);
                return;
            }

            const vectorContext = ol.render.getVectorContext(event);
            const eased = ol.easing.easeOut(t);
            const radius = eased * 50 + 14;
            const opacity = 1 - eased;

            const hcolor1 = replaceHtmlColorOpacity(this.getCustomConfigValueOrDefault('mapFlashColor1', MAPAPP_DEFAULTS.MAP_FLASH_COLOR_1), opacity * 0.9);
            const hcolor2 = replaceHtmlColorOpacity(this.getCustomConfigValueOrDefault('mapFlashColor2', MAPAPP_DEFAULTS.MAP_FLASH_COLOR_2), opacity * 0.9);
            const flashStyle = new ol.style.Style({
                image: new ol.style.Circle({
                    radius: radius,
                    fill: new ol.style.Fill({
                        color: hcolor1
                    }),
                    stroke: new ol.style.Stroke({
                        color: hcolor2,
                        width: 6
                    })
                })
            });

            vectorContext.setStyle(flashStyle);
            vectorContext.drawGeometry(flashGeometry);
        });

        // Trigger re-renders for the animation duration -- of the map the flash started on: a world
        // switch inside the 1.2 s destroys it (this.map becomes null, then the next world's map), and
        // the loop used to call render() on whatever this.map was by then, throwing every frame
        const flashMap = this.map;
        const animate = () => {
            if ((this.map === flashMap) && (Date.now() - start < duration)) {
                flashMap.render();
                requestAnimationFrame(animate);
            }
        };
        animate();
    }



    getCustomConfigValueOrDefault(key, defaultVal) {
        if (this.customConfig && (key in this.customConfig)) {
            return this.customConfig[key];
        }
        return defaultVal;
    }


    /**
     * Fits the map view to the specified bounds.
     * @param {Array<number>} bounds - Extent array [minX, minY, maxX, maxY]
     */
    fitTheseBounds(bounds) {
        if (this.mapMode === MAP_MODES.RASTER) {
            // Raster mode: bounds are already in pixel coordinates
            this.map.getView().fit(bounds, {
                padding: MAP_CONSTANTS.DEFAULT_PADDING,
                duration: MAP_CONSTANTS.ANIMATION_DURATION_DEFAULT
            });
        } else {
            doFitBounds(this.map, bounds);
        }
    }



    // ==========================================
    // ANCHOR MARKER LAYER
    // ==========================================

    /**
     * Updates both anchor layers (ring and POI marker) for the anchored item.
     * @param {string|null} uid - Unique identifier of the anchored POI, or null to clear
     * @param {Array<number>|null} xy - Coordinates [x, y] in Web Mercator, or null to clear
     * @param {Object|null} item - The POI item with geometry data, or null to clear
     */
    updateAnchorLayerAll(uid, xy, item) {
        // ring around the poi
        this._updateAnchorLayerRing(uid, xy);
        // the poi
        this._updateAnchorLayerPoi(item);
    }



    /**
     * Updates the anchor layer with a visual ring at the specified coordinates.
     * Creates a persistent marker ring (distinct from the highlight ring) at the anchor location.
     * If uid is null, clears the anchor layer.
     * @param {string|null} uid - Unique identifier of the anchored POI, or null to clear
     * @param {Array<number>|null} xy - Coordinates [x, y] in Web Mercator, or null to clear
     */
    _updateAnchorLayerRing(uid, xy) {
        const layerName = MAP_AUX_OVERLAY_LAYERS.ANCHOR;

        // Clear anchor if no uid or coordinates provided
        if (!uid || !xy) {
            this.clearResultLayer(layerName);
            return;
        }

        // Create a pseudo-item with geometry for the layer update
        const anchorItem = {
            uid: uid,
            geometry: {
                coordinates: xy
            }
        };

        // Style and zIndex are set during layer creation in createAuxResultLayers()
        this.recreateAllMapFeatureMarkersForLayerWithNewResults(layerName, [anchorItem]);
    }


    /**
     * Updates the anchorPoi layer with the anchor item.
     * This layer holds the actual POI marker for the anchor (not the ring).
     * @param {Object|null} item - The anchor item with geometry, or null to clear
     */
    _updateAnchorLayerPoi(item) {
        const layerName = MAP_AUX_OVERLAY_LAYERS.ANCHOR_POI;

        // Clear the layer if no item provided
        if (!item || !item.geometry || !item.geometry.coordinates) {
            this.clearResultLayer(layerName);
            return;
        }

        // Style and zIndex are set during layer creation in createAuxResultLayers()
        this.recreateAllMapFeatureMarkersForLayerWithNewResults(layerName, [item]);
    }


    /**
     * Updates the visibility of the anchorPoi layer.
     * The layer is shown only if there is an anchor set AND the anchor uid is NOT
     * displayed on any currently visible overlay layer.
     * Should be called when anchor changes, overlay visibility changes, or layer contents change.
     */
    updateAnchorPoiVisibility() {
        const layerName = MAP_AUX_OVERLAY_LAYERS.ANCHOR_POI;
        const layer = this.resultLayers.get(layerName);

        // Get the current anchor from MapApp
        const anchor = this.mapApp?.getCurrentAnchor();
        const anchorUid = anchor?.uid;

        // If no anchor is set, hide the layer
        if (!anchorUid) {
            if (layer) {
                layer.setVisible(false);
            }
            return;
        }

        // Check if the anchor uid is visible on any active overlay layer
        const isVisibleElsewhere = this.isUidVisibleOnAnyActiveLayer(anchorUid);

        // Show the anchorPoi layer only if the anchor is NOT visible elsewhere
        if (layer) {
            layer.setVisible(!isVisibleElsewhere);
        }
    }


    /**
     * Checks if a uid is visible on any currently active (visible) overlay layer.
     * @param {string} uid - The unique identifier to check
     * @returns {boolean} True if the uid is found on any visible layer
     */
    isUidVisibleOnAnyActiveLayer(uid) {
        if (!uid) return false;

        // Get current active tab name from MapApp
        const activeTabName = this.mapApp ? this.mapApp.calcActiveSearcherName() : null;

        // Get overlay visibility state from the OverlayControl
        const overlayState = this.controls.overlayControl
            ? this.controls.overlayControl.getVisibilityState()
            : {};

        // Layer names that have corresponding result layers (POI layers)
        const layerNames = [SEARCH_TYPES.WHITE, SEARCH_TYPES.YELLOW, SEARCH_TYPES.REVERSE, SEARCH_TYPES.ADVANCED, SEARCH_TYPES.BOOKMARKS, SEARCH_TYPES.HISTORY, SEARCH_TYPES.SAVEDSEARCH];

        for (const layerName of layerNames) {
            const layer = this.resultLayers.get(layerName);
            if (!layer) continue;

            // Check if this layer is currently visible
            const isActive = (layerName === activeTabName);
            const isOverlayEnabled = overlayState[layerName] ?? false;
            const isLayerVisible = isActive || isOverlayEnabled;

            if (!isLayerVisible) continue;

            // Check if the uid exists in this layer's features
            const source = layer.getSource();
            if (source) {
                const features = source.getFeatures();
                for (const feature of features) {
                    if (feature.item.uid === uid) {
                        return true;
                    }
                }
            }
        }

        return false;
    }




    // ==========================================
    // REGION OUTLINE LAYER
    // ==========================================


    /**
     * Updates the region layer with the given geometry and style mode.
     * In outline mode, renders a dashed red border around the region.
     * In inverted (dim-outside) mode, dims the area outside the region with a semi-transparent mask
     * and renders a dashed red border around the region.
     * Clears the layer if geometry is null.
     *
     * @param {Object|null} geometry - The polygon geometry object with coordinates, or null to clear
     * @param {boolean} flagDimOutside - If true, use inverted mask style; if false, use outline-only style
     */
    updateRegionLayerStyle(geometry, outsideRegionOpacity) {
        const layerName = MAP_AUX_OVERLAY_LAYERS.REGION;
        const layer = this.getResultLayer(layerName);
        if (!layer) {
            return;
        }

        // Clear the layer if no geometry provided
        if (!geometry || !geometry.coordinates) {
            layer.getSource().clear();
            return;
        }

        // Determine style type: outline-only when opacity is 1.0 (fully visible), inverted mask otherwise
        const flagDimOutside = outsideRegionOpacity < 1.0;
        const dimMaskOpacity = flagDimOutside ? (1.0 - outsideRegionOpacity) : 0;
        const styleType = flagDimOutside ? `inverted_${dimMaskOpacity}` : 'outline';
        if (layer._regionStyleType !== styleType) {
            layer.setStyle(flagDimOutside ? this._makeRegionInvertedStyleFunction(dimMaskOpacity) : this._makeRegionOutlineStyle());
            layer._regionStyleType = styleType;
        }

        // Clear existing features and populate with new ones
        const source = layer.getSource();
        source.clear();

        if (flagDimOutside) {
            this._addInvertedRegionFeatures(source, geometry);
        } else {
            this._addOutlineRegionFeatures(source, geometry);
        }

        layer.setVisible(true);
    }


    /**
     * Adds a simple polygon/multipolygon feature to the source for outline-only display.
     * @param {ol.source.Vector} source - The vector source to add features to
     * @param {Object} geometry - The geometry object with type and coordinates
     * @private
     */
    _addOutlineRegionFeatures(source, geometry) {
        const olGeometry = this._createOlGeometryFromGeoJson(geometry);
        if (!olGeometry) {
            return;
        }

        const feature = new ol.Feature({
            geometry: olGeometry
        });
        source.addFeature(feature);
    }


    /**
     * Adds inverted mask and border features to the source.
     * Creates a large polygon covering the full map extent with the region cut out as a hole,
     * plus a separate border feature for the dashed stroke.
     * @param {ol.source.Vector} source - The vector source to add features to
     * @param {Object} geometry - The geometry object with type and coordinates
     * @private
     */
    _addInvertedRegionFeatures(source, geometry) {
        // Get the extent for the outer ring
        // In raster mode, use the actual image extent (not the padded projection extent)
        // to avoid the mask extending into the padding area above the map
        const projection = this.map.getView().getProjection();
        const mapExtent = this.rasterImageHeight
            ? [0, -this.rasterImageHeight, this.rasterImageWidth, 0]
            : projection.getExtent();
        const outerRing = [
            [mapExtent[0], mapExtent[1]],
            [mapExtent[0], mapExtent[3]],
            [mapExtent[2], mapExtent[3]],
            [mapExtent[2], mapExtent[1]],
            [mapExtent[0], mapExtent[1]] // Close the ring
        ];

        // Build the mask polygon with holes and a separate border polygon
        let maskGeometry;
        let borderGeometry;
        if (geometry.type === 'Polygon') {
            const holeRing = geometry.coordinates[0];
            maskGeometry = new ol.geom.Polygon([outerRing, holeRing]);
            borderGeometry = new ol.geom.Polygon(geometry.coordinates);
        } else if (geometry.type === 'MultiPolygon') {
            const rings = [outerRing];
            for (const polygon of geometry.coordinates) {
                rings.push(polygon[0]);
            }
            maskGeometry = new ol.geom.Polygon(rings);
            borderGeometry = new ol.geom.MultiPolygon(geometry.coordinates);
        } else {
            console.error(`ERROR: Unsupported geometry type for region layer: ${geometry.type}`);
            return;
        }

        // Add mask feature (fill only)
        const maskFeature = new ol.Feature({ geometry: maskGeometry });
        maskFeature.set('featureType', 'mask');
        source.addFeature(maskFeature);

        // Add border feature (dashed stroke only)
        const borderFeature = new ol.Feature({ geometry: borderGeometry });
        borderFeature.set('featureType', 'border');
        source.addFeature(borderFeature);
    }


    /**
     * Creates an OpenLayers geometry from a GeoJSON-like geometry object.
     * @param {Object} geometry - Object with type ('Polygon' or 'MultiPolygon') and coordinates
     * @returns {ol.geom.Geometry|null} The OpenLayers geometry, or null if unsupported type
     * @private
     */
    _createOlGeometryFromGeoJson(geometry) {
        if (geometry.type === 'Polygon') {
            return new ol.geom.Polygon(geometry.coordinates);
        } else if (geometry.type === 'MultiPolygon') {
            return new ol.geom.MultiPolygon(geometry.coordinates);
        }
        console.error(`ERROR: Unsupported geometry type for region layer: ${geometry.type}`);
        return null;
    }


    /**
     * Creates the region outline style (dashed red border, no fill).
     * @returns {ol.style.Style} The outline style
     * @private
     */
    _makeRegionOutlineStyle() {
        const dashLineWidth = this.getCustomConfigValueOrDefault('regionBoundaryLineDashWidth', MAPAPP_DEFAULTS.REGION_BOUNDARY_LINE_DASH_WIDTH);
        const regionLineColor = this.getCustomConfigValueOrDefault('regionBoundaryLineColor', MAP_CONSTANTS.REGION_BOUNDARY_COLOR);
        const regionLineAlpha = 0.9;
        const regionLineWidth = this.getCustomConfigValueOrDefault('regionBoundaryLineWidth', MAPAPP_DEFAULTS.REGION_BOUNDARY_LINE_WIDTH);
        //const regionBoundaryDimOpacity = this.getCustomConfigValueOrDefault('regionBoundaryDimOpacity', MAPAPP_DEFAULTS.REGION_BOUNDARY_DIM_OPACITY);

        //
        return new ol.style.Style({
            stroke: new ol.style.Stroke({
                color: replaceHtmlColorOpacity(regionLineColor, regionLineAlpha),
                width: regionLineWidth,
                lineDash: [dashLineWidth, dashLineWidth]
            }),
        });
    }


    /**
     * Creates the region inverted style function.
     * Returns different styles for mask vs border features.
     * @returns {Function} Style function for inverted region display
     * @private
     */
    _makeRegionInvertedStyleFunction(dimMaskOpacity) {
        const dashLineWidth = this.getCustomConfigValueOrDefault('regionBoundaryLineDashWidth', MAPAPP_DEFAULTS.REGION_BOUNDARY_LINE_DASH_WIDTH);
        const regionLineColor = this.getCustomConfigValueOrDefault('regionBoundaryLineColor', MAP_CONSTANTS.REGION_BOUNDARY_COLOR);
        const regionLineAlpha = 0.9;
        const regionLineWidth = this.getCustomConfigValueOrDefault('regionBoundaryLineWidth', MAPAPP_DEFAULTS.REGION_BOUNDARY_LINE_WIDTH);

        return (feature) => {
            const featureType = feature.get('featureType');
            if (featureType === 'mask') {
                return new ol.style.Style({
                    fill: new ol.style.Fill({
                        color: `rgba(0, 0, 0, ${dimMaskOpacity})`
                    })
                });
            } else if (featureType === 'border') {
                return new ol.style.Style({
                    stroke: new ol.style.Stroke({
                color: replaceHtmlColorOpacity(regionLineColor, regionLineAlpha),
                        width: regionLineWidth,
                        lineDash: [dashLineWidth, dashLineWidth]
                    })
                });
            }
        };
    }



    refreshAllFeaturesWithUid(uid) {
        // the dynamic marker style function used by features with this item.uid may have changed based on a change to item fields
        // go through all visible layers, for all features with item.uid == uid, reset(remake) the feature style, since it may have changed (now bookmarked)
        for (const [layerName, layer] of this.resultLayers) {
            const source = layer.getSource();
            if (!source) continue;
            const features = source.getFeatures();
            for (const feature of features) {
                if (feature.item && feature.item.uid === uid) {
                    // clear per-feature style so the layer's style function re-evaluates
                    feature.setStyle(null);
                    feature.changed();
                }
            }
        }
    }


    lookupMarkerPcatgImageForItem(item) {
        const imagePath = this.mapApp.getImageHelper().calcUserImagePathForItem(item)
        return imagePath;
    }



    setFirstBounds(firstBounds) {
        //const firstBoundsComputed = computeTileGridExtent(firstBounds, 0.5, 50);
        const firstBoundsComputed = firstBounds
        // Store first bounds for initial view, but only fit to bounds if no saved state
        // (saved state was already applied during map initialization)
        const scaleMultiplier = this.getCustomConfigValueOrDefault('mapInitialZoomScale', MAPAPP_DEFAULTS.INITIAL_MAP_ZOOM_SCALE);

        if (firstBounds) {
            let firstBoundsScaled = scaleExtent(firstBoundsComputed, scaleMultiplier);
            this.firstBounds = firstBoundsScaled;
            // Only fit to default bounds if no saved view exists
            if (!this.mapApp.getStateManager().getMapView()) {
                this.fitTheseBounds(firstBoundsScaled);
            }
        }
    }


    resetToFitBoundsInitial() {
        if (this.firstBounds) {
            this.fitTheseBounds(this.firstBounds);
        }
    }


    // ==========================================
    // DEBUG LABELS
    // ==========================================


    /**
     * Add a text label to the debug labels layer at a given geometry point.
     * Creates the layer on first call.
     * @param {string} labelText - The text to display
     * @param {Array<number>} coordinates - [x, y] coordinates in map projection
     */
    addDebugLabel(labelText, coordinates) {
        const layerName = MAP_AUX_OVERLAY_LAYERS.DEBUG;

        // Create the debug labels layer if it doesn't exist yet
        if (!this.resultLayers.has(layerName)) {
            const labelStyle = (feature) => {
                return new ol.style.Style({
                    text: new ol.style.Text({
                        text: feature.get('label'),
                        font: 'bold 24px sans-serif',
                        fill: new ol.style.Fill({ color: 'rgba(255, 255, 0, 0.7)' }),
                        stroke: new ol.style.Stroke({ color: 'rgba(200, 0, 0, 0.8)', width: 2 }),
                        offsetY: -10,
                    }),
                    image: new ol.style.Circle({
                        radius: 3,
                        fill: new ol.style.Fill({ color: '#f00' }),
                    }),
                });
            };
            this.createResultLayer(layerName, {
                visible: true,
                zIndex: MAP_LAYER_ZINDICES.DEFAULT_GENERIC + 10,
                style: labelStyle,
            });
        }

        const layer = this.resultLayers.get(layerName);
        const feature = new ol.Feature({
            geometry: new ol.geom.Point(coordinates),
            label: labelText,
        });
        layer.getSource().addFeature(feature);
    }


}
